import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Flex, Text, Tabs, TabList, Tab, TabPanels, TabPanel, Icon,
    Checkbox,
    FormControl, FormLabel, Input, useToast, VStack, HStack, Spinner, SimpleGrid,
    Table, Thead, Tbody, Tr, Th, Td, Badge, Select, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Switch, Textarea,
    InputGroup, InputLeftElement, Tag, TagLabel, TagCloseButton, Progress, Stack, Divider, Tooltip,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
    Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody, PopoverHeader,
    PopoverCloseButton, IconButton, Image, Wrap, WrapItem,
} from '@chakra-ui/react';
import { SearchIcon, CopyIcon, QuestionOutlineIcon } from '@chakra-ui/icons';
import { FaRulerCombined, FaExclamationTriangle, FaLayerGroup, FaHistory, FaChartBar, FaFlask, FaBoxOpen, FaCog, FaArrowDown, FaBalanceScale, FaClipboardCheck } from 'react-icons/fa';
import ScrollToTop from '../components/ScrollToTop';
import {
    getDimensoesBanco, getDimensoesSuspeitos, getDimensoesPlacar, getDimensoesLogs, getDimensoesCotacoes, getDimensoesConferencia,
    simularDimensao, medirDimensao, getDimensaoFamilias, salvarDimensaoFamilia,
    previaDimensaoFamilia, getProdutosDaFamilia, vincularProdutoFamilia,
    getDimensaoConfig, salvarDimensaoConfig,
} from '../services/api';

const COR_ORIGEM = { 0: 'gray', 1: 'purple', 2: 'blue', 3: 'green', 4: 'orange' };

const SeloOrigem = ({ origem, rotulos }) => (
    <Badge colorScheme={COR_ORIGEM[origem] || 'gray'} fontSize="10px" borderRadius="md" px={2} py={0.5} whiteSpace="nowrap">
        {(rotulos && rotulos[origem]) || 'Sem procedência'}
    </Badge>
);

const kg = (v) => (v === null || v === undefined || v === '' ? '-' : `${Number(v).toFixed(3)} kg`);
const num = (v) => Number(v || 0).toLocaleString('pt-BR');

const dataHora = (v) => {
    if (!v) return '-';
    const d = new Date(String(v).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const dataCurta = (v) => {
    if (!v) return '-';
    const d = new Date(String(v).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleDateString('pt-BR');
};

const CONFIANCA = {
    alta: {
        cor: 'green',
        texto: 'Confiança alta',
        resumo: 'as duas leituras chegaram a valores próximos',
    },
    media: {
        cor: 'yellow',
        texto: 'Confiança média',
        resumo: 'só uma das leituras respondeu, não há como conferir uma contra a outra',
    },
    baixa: {
        cor: 'red',
        texto: 'Confiança baixa',
        resumo: 'as duas leituras discordam muito entre si',
    },
};

const SeloConfianca = ({ nivel, estimativa }) => {
    const c = CONFIANCA[nivel];
    if (!c) return null;

    const texto = estimativa?.texto_kg;
    const visao = estimativa?.visao_kg;
    const temAsDuas = texto && visao;

    return (
        <Box>
            <Badge colorScheme={c.cor} fontSize="10px" borderRadius="md" px={2} py={0.5}>
                {c.texto}
            </Badge>
            <Text fontSize="xs" color="gray.500" mt={1} maxW="320px">
                {temAsDuas ? (
                    <>
                        O texto estimou <b>{Number(texto).toFixed(1)} kg</b> e a foto{' '}
                        <b>{Number(visao).toFixed(1)} kg</b>
                        {nivel === 'baixa'
                            ? '. Quando a diferença é grande assim, a estimativa erra feio em 41% das vezes, contra 28% quando as duas concordam.'
                            : '. Valores próximos, o que costuma indicar acerto.'}
                    </>
                ) : (
                    <>
                        {texto ? 'Só o texto respondeu' : 'Só a foto respondeu'}, então não dá para conferir uma
                        leitura contra a outra. Vale medir a peça antes de confiar.
                    </>
                )}
            </Text>
        </Box>
    );
};

const Miniatura = ({ url, nome }) => (
    url ? (
        <Image
            src={url} alt={nome} boxSize="40px" objectFit="contain" borderRadius="md"
            bg="gray.50" border="1px solid" borderColor="gray.100" flexShrink={0}
            fallback={<Box boxSize="40px" bg="gray.100" borderRadius="md" flexShrink={0} />}
        />
    ) : (
        <Flex boxSize="40px" bg="gray.50" borderRadius="md" align="center" justify="center" flexShrink={0}
            border="1px dashed" borderColor="gray.200">
            <Text fontSize="9px" color="gray.400">sem foto</Text>
        </Flex>
    )
);

const Cartao = ({ children, ...props }) => (
    <Box bg="white" borderRadius="2xl" boxShadow="sm" border="1px solid" borderColor="gray.100" {...props}>
        {children}
    </Box>
);

const Indicador = ({ rotulo, valor, ajuda, cor }) => (
    <Cartao p={{ base: 4, md: 5 }}>
        <Text fontSize="xs" color="gray.500" fontWeight="medium" noOfLines={2} minH="32px">{rotulo}</Text>
        <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color={cor || 'gray.800'} lineHeight="1.2">
            {valor}
        </Text>
        {ajuda && <Text fontSize="xs" color="gray.500" mt={1} noOfLines={2}>{ajuda}</Text>}
    </Cartao>
);

const Rolavel = ({ children }) => (
    <Box overflowX="auto" w="100%">
        <Box minW={{ base: '640px', lg: 'auto' }}>{children}</Box>
    </Box>
);

const ModalMedir = ({ produto, aberto, aoFechar, aoSalvar }) => {
    const [form, setForm] = useState({ altura: '', largura: '', comprimento: '', peso: '', observacao: '' });
    const [salvando, setSalvando] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (produto) {
            setForm({
                altura: produto.altura || '', largura: produto.largura || '',
                comprimento: produto.comprimento || '', peso: produto.peso || '', observacao: '',
            });
        }
    }, [produto]);

    const cobravel = () => {
        const { altura, largura, comprimento, peso } = form;
        if (!altura || !largura || !comprimento) return null;
        return Math.max(Number(peso || 0), (Number(altura) * Number(largura) * Number(comprimento)) / 6000);
    };

    const salvar = async () => {
        setSalvando(true);
        try {
            const res = await medirDimensao(produto.id, form);
            const avisos = res?.avisos || [];
            toast({
                title: 'Medida registrada', description: avisos.length ? avisos.join(' ') : undefined,
                status: 'success', duration: avisos.length ? 8000 : 3000, isClosable: true,
            });
            aoSalvar();
            aoFechar();
        } catch (err) {
            toast({
                title: 'Não foi possível salvar', description: err.response?.data?.error || err.message,
                status: 'error', duration: 5000, isClosable: true,
            });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal isOpen={aberto} onClose={aoFechar} size={{ base: 'full', md: 'lg' }} isCentered>
            <ModalOverlay />
            <ModalContent borderRadius={{ base: 0, md: '2xl' }}>
                <ModalHeader fontSize="md">Medir dimensão</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Text fontSize="sm" color="gray.600" mb={4} noOfLines={2}>
                        {produto?.nome} <Badge ml={1} fontFamily="mono">#{produto?.id}</Badge>
                    </Text>
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                        {['altura', 'largura', 'comprimento'].map((campo) => (
                            <FormControl key={campo}>
                                <FormLabel fontSize="xs" textTransform="capitalize" mb={1}>{campo} (cm)</FormLabel>
                                <Input
                                    size="sm" type="number" borderRadius="lg" value={form[campo]}
                                    onChange={(e) => setForm((p) => ({ ...p, [campo]: e.target.value }))}
                                />
                            </FormControl>
                        ))}
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Peso (kg)</FormLabel>
                            <Input
                                size="sm" type="number" borderRadius="lg" value={form.peso}
                                onChange={(e) => setForm((p) => ({ ...p, peso: e.target.value }))}
                            />
                        </FormControl>
                    </SimpleGrid>
                    {cobravel() !== null && (
                        <Box mt={3} p={3} bg="orange.50" borderRadius="lg">
                            <Text fontSize="sm">Peso cobrável resultante: <b>{cobravel().toFixed(3)} kg</b></Text>
                        </Box>
                    )}
                    <FormControl mt={4}>
                        <FormLabel fontSize="xs" mb={1}>Observação</FormLabel>
                        <Textarea
                            size="sm" rows={2} borderRadius="lg" value={form.observacao}
                            placeholder="Como foi medido, com o que, o que chamou atenção"
                            onChange={(e) => setForm((p) => ({ ...p, observacao: e.target.value }))}
                        />
                    </FormControl>
                    <Text fontSize="xs" color="gray.500" mt={3}>
                        Entra como <b>Medido pela TAGA</b> e fica registrado no log com seu usuário.
                    </Text>
                </ModalBody>
                <ModalFooter>
                    <Button size="sm" variant="ghost" mr={2} onClick={aoFechar}>Cancelar</Button>
                    <Button size="sm" colorScheme="orange" borderRadius="lg" onClick={salvar} isLoading={salvando}>
                        Salvar medida
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const BlocoJson = ({ dado }) => {
    const toast = useToast();
    const texto = JSON.stringify(dado, null, 2);

    const copiar = () => {
        navigator.clipboard.writeText(texto);
        toast({ title: 'JSON copiado', status: 'success', duration: 2000, isClosable: true });
    };

    return (
        <Cartao overflow="hidden">
            <Accordion allowToggle>
                <AccordionItem border="none">
                    <AccordionButton px={{ base: 4, md: 5 }} py={3} _hover={{ bg: 'gray.50' }}>
                        <Box flex="1" textAlign="left">
                            <Text fontSize="sm" fontWeight="bold">Resposta completa da API</Text>
                            <Text fontSize="xs" color="gray.500">o mesmo JSON que o endpoint devolve</Text>
                        </Box>
                        <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel px={{ base: 3, md: 5 }} pb={5} pt={0}>
                        <Flex justify="flex-end" mb={2}>
                            <Button size="xs" leftIcon={<CopyIcon />} variant="outline" borderRadius="lg" onClick={copiar}>
                                Copiar
                            </Button>
                        </Flex>
                        <Box
                            as="pre" bg="gray.900" color="green.200" p={4} borderRadius="xl"
                            fontSize="xs" fontFamily="mono" overflowX="auto" maxH="460px" overflowY="auto"
                            whiteSpace="pre" sx={{ tabSize: 2 }}
                        >
                            {texto}
                        </Box>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </Cartao>
    );
};

const LadoEstimador = ({ titulo, dados, destaque, vazio }) => (
    <Box
        flex={1} minW={0} borderWidth="1px" borderRadius="xl" p={4}
        borderColor={destaque ? 'orange.200' : 'gray.200'} bg={destaque ? 'orange.50' : 'white'}
    >
        <Text fontSize="xs" color="gray.500">{titulo}</Text>
        {dados ? (
            <>
                <Text fontSize="xl" fontWeight="bold" color={destaque ? 'orange.500' : 'gray.700'} lineHeight="1.2">
                    {Number(dados.cobravel).toFixed(3)} kg
                </Text>
                <Text fontSize="xs" color="gray.600">
                    {Number(dados.altura).toFixed(1)} x {Number(dados.largura).toFixed(1)} x {Number(dados.comprimento).toFixed(1)} cm
                </Text>
                <Text fontSize="10px" color="gray.500" mt={1} fontFamily="mono">{dados.metodo}</Text>
                {dados.nome_estimado && (
                    <Text fontSize="10px" color="gray.500" noOfLines={1}>copiou de: {dados.nome_estimado}</Text>
                )}
            </>
        ) : (
            <Text fontSize="sm" color="gray.400" mt={2}>{vazio}</Text>
        )}
    </Box>
);

const Comparacao = ({ dados }) => {
    const r = dados.razao;
    const leitura = !r ? null
        : r > 1.05 ? `o novo cota ${((r - 1) * 100).toFixed(0)}% a mais que o antigo`
            : r < 0.95 ? `o novo cota ${((1 - r) * 100).toFixed(0)}% a menos que o antigo`
                : 'os dois chegam praticamente no mesmo valor';

    return (
        <Cartao p={{ base: 4, md: 5 }}>
            <Text fontSize="sm" fontWeight="bold" mb={3}>Estimador antigo contra o novo</Text>
            <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align="stretch">
                <LadoEstimador titulo="Antigo" dados={dados.antigo} vazio={dados.erro || 'não respondeu'} />
                <LadoEstimador titulo="Novo (v2), em produção" dados={dados.novo} destaque />
            </Stack>
            {leitura && <Text fontSize="xs" color="gray.500" mt={3}>{leitura}. Peso cobrável, que é o que o frete usa.</Text>}
        </Cartao>
    );
};

const AbaSimulador = () => {
    const [sku, setSku] = useState('');
    const [dado, setDado] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const toast = useToast();

    const simular = async () => {
        if (!sku.trim()) return;
        setCarregando(true);
        try {
            setDado(await simularDimensao(sku.trim()));
        } catch (err) {
            setDado(null);
            toast({
                title: 'Não foi possível simular', description: err.response?.data?.error || err.message,
                status: 'error', duration: 4000, isClosable: true,
            });
        } finally {
            setCarregando(false);
        }
    };

    const e = dado?.estimativa;

    return (
        <VStack align="stretch" spacing={4}>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={2}>
                <InputGroup maxW={{ base: '100%', sm: '360px' }}>
                    <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
                    <Input
                        bg="white" borderRadius="lg" placeholder="SKU do produto (ex.: 72618)" value={sku}
                        onChange={(ev) => setSku(ev.target.value)}
                        onKeyDown={(ev) => ev.key === 'Enter' && simular()}
                    />
                </InputGroup>
                <Button colorScheme="orange" borderRadius="lg" onClick={simular} isLoading={carregando} px={8}>
                    Estimar
                </Button>
            </Stack>

            {dado && (
                <>
                    <Cartao p={{ base: 4, md: 5 }}>
                        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={4}>
                            <Box minW={0} flex="1 1 260px">
                                <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color="gray.800" noOfLines={2}>
                                    {dado.produto.nome}
                                </Text>
                                <HStack mt={2} spacing={2} wrap="wrap">
                                    <Badge fontFamily="mono">#{dado.produto.id}</Badge>
                                    {dado.produto.codigo_global && <Badge variant="outline">{dado.produto.codigo_global}</Badge>}
                                    <SeloOrigem
                                        origem={dado.produto.origem}
                                        rotulos={{ [dado.produto.origem]: dado.produto.origem_rotulo }}
                                    />
                                </HStack>
                            </Box>
                            <Box textAlign={{ base: 'left', sm: 'right' }} flex="0 0 auto">
                                <Text fontSize="xs" color="gray.500">Estimativa</Text>
                                <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color="orange.500" lineHeight="1.1">
                                    {Number(e.peso).toFixed(3)} kg
                                </Text>
                                <Text fontSize="sm" color="gray.600">
                                    {Number(e.altura).toFixed(1)} x {Number(e.largura).toFixed(1)} x {Number(e.comprimento).toFixed(1)} cm
                                </Text>
                                {e.confianca && (
                                    <Flex mt={3} justify={{ base: 'flex-start', sm: 'flex-end' }}>
                                        <SeloConfianca nivel={e.confianca} estimativa={e} />
                                    </Flex>
                                )}
                            </Box>
                        </Flex>
                    </Cartao>

                    {dado.comparacao && <Comparacao dados={dado.comparacao} />}

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                        <Cartao p={4}>
                            <Text fontSize="xs" color="gray.500">Método</Text>
                            <Text fontWeight="bold" fontSize="sm">{e.metodo}</Text>
                            {e.piso_aplicado && (
                                <Text fontSize="xs" color="purple.600" mt={1}>piso da família: {e.piso_aplicado} kg</Text>
                            )}
                        </Cartao>
                        <Cartao p={4}>
                            <Text fontSize="xs" color="gray.500">Perna de texto</Text>
                            <Text fontWeight="bold" fontSize="sm">{kg(e.texto_kg)}</Text>
                            <Text fontSize="xs" color="gray.500" mt={1}>modelo treinado no catálogo</Text>
                        </Cartao>
                        <Cartao p={4}>
                            <Text fontSize="xs" color="gray.500">Perna de visão</Text>
                            <Text fontWeight="bold" fontSize="sm">{kg(e.visao_kg)}</Text>
                            {e.visao ? (
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    foto lida: {e.visao.altura_cm}x{e.visao.largura_cm}x{e.visao.comprimento_cm} cm, {e.visao.peso_kg} kg
                                </Text>
                            ) : (
                                <Text fontSize="xs" color="red.400" mt={1}>produto sem foto</Text>
                            )}
                        </Cartao>
                    </SimpleGrid>

                    {dado.familias?.length > 0 && (
                        <Cartao p={{ base: 4, md: 5 }}>
                            <Text fontSize="sm" fontWeight="bold" mb={3}>Famílias que alcançam este produto</Text>
                            <VStack align="stretch" spacing={2}>
                                {dado.familias.map((f) => (
                                    <Flex key={f.nome} align="center" gap={3} wrap="wrap">
                                        <Badge colorScheme="purple" borderRadius="md" px={2}>{f.nome}</Badge>
                                        <Text fontSize="sm">piso {f.piso_kg} kg</Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {f.vinculo_manual === false ? 'excluído manualmente'
                                                : f.vinculo_manual === true ? 'incluído manualmente'
                                                    : f.casa_padrao ? 'pelo padrão do nome' : ''}
                                        </Text>
                                    </Flex>
                                ))}
                            </VStack>
                        </Cartao>
                    )}

                    <Cartao p={{ base: 4, md: 5 }}>
                        <Text fontSize="sm" fontWeight="bold" mb={3}>Ficha do produto</Text>
                        <Flex gap={5} direction={{ base: 'column', md: 'row' }}>
                            <Box flexShrink={0}>
                                {dado.produto.imagens?.length > 0 ? (
                                    <VStack align="flex-start" spacing={2}>
                                        <Image
                                            src={dado.produto.imagens[0]} alt={dado.produto.nome}
                                            boxSize={{ base: '100%', md: '180px' }} maxW="260px" objectFit="contain"
                                            bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.100"
                                        />
                                        {dado.produto.imagens.length > 1 && (
                                            <Wrap spacing={2}>
                                                {dado.produto.imagens.slice(1).map((u, i) => (
                                                    <WrapItem key={i}>
                                                        <Image
                                                            src={u} alt="" boxSize="42px" objectFit="contain" bg="gray.50"
                                                            borderRadius="md" border="1px solid" borderColor="gray.100"
                                                        />
                                                    </WrapItem>
                                                ))}
                                            </Wrap>
                                        )}
                                    </VStack>
                                ) : (
                                    <Flex
                                        boxSize={{ base: '100%', md: '180px' }} bg="gray.50" borderRadius="xl"
                                        align="center" justify="center" border="1px dashed" borderColor="gray.200"
                                    >
                                        <Text fontSize="xs" color="gray.400">produto sem foto</Text>
                                    </Flex>
                                )}
                            </Box>

                            <Box flex={1} minW={0}>
                                <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={6} spacingY={0}>
                                    <LinhaFicha rotulo="Fabricante">{dado.produto.estoque?.nome_fabricante || dado.produto.modelo || '-'}</LinhaFicha>
                                    <LinhaFicha rotulo="Fornecedor">{dado.produto.estoque?.fornecedor || '-'}</LinhaFicha>
                                    <LinhaFicha rotulo="Código do fabricante">{dado.produto.estoque?.codigo_fabricante || '-'}</LinhaFicha>
                                    <LinhaFicha rotulo="Código do fornecedor">{dado.produto.estoque?.codigo_fornecedor || '-'}</LinhaFicha>
                                    <LinhaFicha rotulo="Similares">{dado.produto.codigo_similares || '-'}</LinhaFicha>
                                    <LinhaFicha rotulo="NCM">{dado.produto.estoque?.ncm || '-'}</LinhaFicha>
                                    <LinhaFicha rotulo="Preço de venda">
                                        {dado.produto.preco ? 'R$ ' + Number(dado.produto.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '-'}
                                    </LinhaFicha>
                                    <LinhaFicha rotulo="Estoque">
                                        {dado.produto.estoque ? (Number(dado.produto.estoque.quantidade || 0) + ' fornecedor · ' + Number(dado.produto.estoque.quantidade_taga || 0) + ' TAGA') : '-'}
                                    </LinhaFicha>
                                    <LinhaFicha rotulo="Vendas">
                                        {dado.produto.vendas?.total > 0
                                            ? dado.produto.vendas.total + ' (última em ' + dataCurta(dado.produto.vendas.ultima) + ')'
                                            : 'nunca vendeu'}
                                    </LinhaFicha>
                                    <LinhaFicha rotulo="Cadastrado em">{dataCurta(dado.produto.criado_em)}</LinhaFicha>
                                    <LinhaFicha rotulo="Dimensão no cadastro">
                                        {dado.produto.cadastro.altura}x{dado.produto.cadastro.largura}x{dado.produto.cadastro.comprimento} cm · {dado.produto.cadastro.peso} kg
                                    </LinhaFicha>
                                    <LinhaFicha rotulo="Medida registrada em">
                                        {dado.produto.medido_em ? dataHora(dado.produto.medido_em) + (dado.produto.medido_por ? ' por ' + dado.produto.medido_por : '') : 'nunca'}
                                    </LinhaFicha>
                                </SimpleGrid>
                                {dado.produto.descricao && (
                                    <Box mt={3}>
                                        <Text fontSize="xs" color="gray.500" mb={1}>Aplicação</Text>
                                        <Text fontSize="xs" color="gray.700" noOfLines={4}>{dado.produto.descricao}</Text>
                                    </Box>
                                )}
                            </Box>
                        </Flex>
                    </Cartao>

                    {dado.medidas_reais?.length > 0 && (
                        <Cartao p={{ base: 4, md: 5 }}>
                            <Text fontSize="sm" fontWeight="bold" mb={3}>Caixas reais já despachadas</Text>
                            <Rolavel>
                                <Table size="sm" variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>Fonte</Th><Th>Caixa</Th><Th isNumeric>Peso</Th>
                                            <Th isNumeric>Cobrável</Th><Th>Quando</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {dado.medidas_reais.map((m) => (
                                            <Tr key={m.id}>
                                                <Td><Badge colorScheme={m.fonte === 'mercado_livre' ? 'purple' : 'blue'}>{m.fonte}</Badge></Td>
                                                <Td whiteSpace="nowrap">{m.altura}x{m.largura}x{m.comprimento} cm</Td>
                                                <Td isNumeric>{m.peso} kg</Td>
                                                <Td isNumeric fontWeight="bold">{m.peso_faturado} kg</Td>
                                                <Td fontSize="xs" whiteSpace="nowrap">{m.medido_em}</Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Rolavel>
                        </Cartao>
                    )}

                    <BlocoJson dado={dado} />
                </>
            )}

            {!dado && !carregando && (
                <Cartao p={10}>
                    <VStack spacing={2} color="gray.400">
                        <Icon as={FaFlask} boxSize={8} />
                        <Text fontSize="sm">Digite um SKU para ver como o estimador chega ao número</Text>
                    </VStack>
                </Cartao>
            )}
        </VStack>
    );
};

const AbaPlacar = () => {
    const [dado, setDado] = useState(null);

    useEffect(() => { getDimensoesPlacar().then(setDado).catch(() => setDado(null)); }, []);

    if (!dado) return <Flex justify="center" py={12}><Spinner color="orange.500" /></Flex>;

    const cob = dado.cobertura || {};
    const af = dado['aferição'] || {};
    const total = Object.values(dado.origens || {}).reduce((a, b) => a + Number(b), 0);
    const confiaveis = Number(dado.origens?.[1] || 0) + Number(dado.origens?.[2] || 0) + Number(dado.origens?.[3] || 0);

    return (
        <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
                <Indicador
                    rotulo="Produtos com medida confiável" valor={num(confiaveis)} cor="green.500"
                    ajuda={`de ${num(total)} produtos`}
                />
                <Indicador
                    rotulo="Com foto lida" valor={num(cob.com_foto)}
                    ajuda={cob.total ? `${Math.round((100 * cob.com_foto) / cob.total)}% do catálogo` : ''}
                />
                <Indicador
                    rotulo="Abaixo do piso físico" valor={num(cob.abaixo_piso)} cor="red.500"
                    ajuda="estimativa impossível no nosso despacho"
                />
                <Indicador
                    rotulo="Erro contra caixa real" valor={af.mediana ?? '-'}
                    ajuda={`${af.amostras} amostras, ${af.dentro_25pct}% dentro de 25%`}
                />
            </SimpleGrid>

            <Cartao p={{ base: 4, md: 5 }}>
                <Text fontSize="sm" fontWeight="bold" mb={4}>Origem da dimensão</Text>
                <VStack align="stretch" spacing={3}>
                    {Object.entries(dado.origens || {}).map(([origem, n]) => (
                        <Box key={origem}>
                            <Flex justify="space-between" align="center" mb={1.5} gap={2}>
                                <SeloOrigem origem={Number(origem)} rotulos={dado.rotulos} />
                                <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">{num(n)}</Text>
                            </Flex>
                            <Progress
                                value={(100 * Number(n)) / (total || 1)} size="xs" borderRadius="full"
                                colorScheme={COR_ORIGEM[origem] || 'gray'}
                            />
                        </Box>
                    ))}
                </VStack>
            </Cartao>

            <Cartao p={{ base: 4, md: 5 }}>
                <Text fontSize="sm" fontWeight="bold" mb={3}>Medidas reais coletadas</Text>
                <Rolavel>
                    <Table size="sm">
                        <Thead>
                            <Tr><Th>Fonte</Th><Th isNumeric>Medidas</Th><Th isNumeric>Produtos</Th><Th isNumeric>Limpas</Th></Tr>
                        </Thead>
                        <Tbody>
                            {(dado.medidas_reais || []).map((m) => (
                                <Tr key={m.fonte}>
                                    <Td><Badge colorScheme={m.fonte === 'mercado_livre' ? 'purple' : 'blue'}>{m.fonte}</Badge></Td>
                                    <Td isNumeric>{m.medidas}</Td>
                                    <Td isNumeric>{m.produtos}</Td>
                                    <Td isNumeric>{m.limpas}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Rolavel>
                <Text fontSize="xs" color="gray.500" mt={3}>
                    A aferição compara o que o estimador previa com a caixa que realmente saiu. Mediana 1,00 seria acerto perfeito.
                </Text>
            </Cartao>
        </VStack>
    );
};

const MOTIVO = {
    abaixo_piso: { cor: 'red', texto: 'Peso impossível' },
    familia_pesada: { cor: 'purple', texto: 'Peça pesada estimada leve' },
    discordancia: { cor: 'orange', texto: 'Texto e foto discordam' },
};

const TabelaProdutos = ({ itens, rotulos, aoMedir, mostrarVendas = false, mostrarMotivo = false }) => (
    <Rolavel>
        <Table size="sm" variant="simple">
            <Thead bg="gray.50">
                <Tr>
                    <Th>Produto</Th>
                    {mostrarMotivo ? <Th>Por que está aqui</Th> : <Th>Origem</Th>}
                    <Th isNumeric>Estimativa</Th>
                    {mostrarVendas && <Th isNumeric>Vendas</Th>}
                    <Th>Visão</Th>
                    <Th></Th>
                </Tr>
            </Thead>
            <Tbody>
                {itens.map((p) => (
                    <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                        <Td maxW="360px">
                            <Flex gap={3} align="center">
                                <Miniatura url={p.imagem} nome={p.nome} />
                                <Box minW={0}>
                                    <Text fontSize="sm" noOfLines={1}>{p.nome}</Text>
                                    <HStack spacing={1} mt={0.5}>
                                        <Badge fontSize="9px" fontFamily="mono">#{p.id}</Badge>
                                        {p.codigo_global && <Badge fontSize="9px" variant="outline">{p.codigo_global}</Badge>}
                                    </HStack>
                                </Box>
                            </Flex>
                        </Td>
                        {mostrarMotivo ? (
                            <Td maxW="320px">
                                <Badge colorScheme={MOTIVO[p.motivo]?.cor || 'gray'} fontSize="9px" borderRadius="md" mb={1}>
                                    {MOTIVO[p.motivo]?.texto || 'Suspeito'}
                                </Badge>
                                <Text fontSize="xs" color="gray.600" noOfLines={2}>{p.motivo_texto}</Text>
                            </Td>
                        ) : (
                            <Td>
                                <SeloOrigem origem={p.origem} rotulos={rotulos} />
                                {p.bloqueio_causa === 'sem_dimensao' && (
                                    <Badge display="block" w="fit-content" mt={1} colorScheme="red" fontSize="9px" borderRadius="md">
                                        Venda bloqueada
                                    </Badge>
                                )}
                            </Td>
                        )}
                        <Td isNumeric fontSize="sm" whiteSpace="nowrap">
                            <Text
                                color={Number(p.cobravel_texto) < 0.15 ? 'red.500' : undefined}
                                fontWeight={Number(p.cobravel_texto) < 0.15 ? 'bold' : undefined}
                            >
                                {kg(p.cobravel_texto)}
                            </Text>
                        </Td>
                        {mostrarVendas && <Td isNumeric fontSize="sm">{p.vendas}</Td>}
                        <Td>
                            {p.visao_peso
                                ? <Badge colorScheme="green" fontSize="9px">lida</Badge>
                                : p.tem_imagem
                                    ? <Badge colorScheme="yellow" fontSize="9px">não lida</Badge>
                                    : <Badge fontSize="9px">sem foto</Badge>}
                        </Td>
                        <Td textAlign="right">
                            <Button size="xs" colorScheme="orange" variant="outline" borderRadius="lg" onClick={() => aoMedir(p)}>
                                Medir
                            </Button>
                        </Td>
                    </Tr>
                ))}
                {itens.length === 0 && (
                    <Tr><Td colSpan={6}>
                        <Text py={6} textAlign="center" color="gray.400" fontSize="sm">Nada por aqui</Text>
                    </Td></Tr>
                )}
            </Tbody>
        </Table>
    </Rolavel>
);

const AbaBanco = () => {
    const [dado, setDado] = useState({ data: [], total: 0, rotulos: {} });
    const [filtros, setFiltros] = useState({ busca: '', origem: 'todas', sem_foto: false });
    const [carregando, setCarregando] = useState(false);
    const [produtoMedir, setProdutoMedir] = useState(null);

    const buscar = useCallback(async () => {
        setCarregando(true);
        try { setDado(await getDimensoesBanco({ ...filtros, limite: 50 })); } finally { setCarregando(false); }
    }, [filtros]);

    useEffect(() => { buscar(); }, [buscar]);

    return (
        <VStack align="stretch" spacing={4}>
            <Stack direction={{ base: 'column', lg: 'row' }} spacing={3} align={{ lg: 'center' }}>
                <InputGroup maxW={{ base: '100%', lg: '320px' }}>
                    <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
                    <Input
                        bg="white" borderRadius="lg" placeholder="Nome, código ou SKU" value={filtros.busca}
                        onChange={(e) => setFiltros((p) => ({ ...p, busca: e.target.value }))}
                    />
                </InputGroup>
                <Select
                    bg="white" borderRadius="lg" maxW={{ base: '100%', lg: '280px' }} value={filtros.origem}
                    onChange={(e) => setFiltros((p) => ({ ...p, origem: e.target.value }))}
                >
                    <option value="todas">Todas as origens</option>
                    <option value="fila">Fila de medição (provisórias e bloqueadas)</option>
                    <option value="0">Sem procedência</option>
                    <option value="1">Medido pelo Mercado Livre</option>
                    <option value="2">Caixa do despacho</option>
                    <option value="3">Medido pela TAGA</option>
                    <option value="4">Provisória (regra do fornecedor)</option>
                </Select>
                <HStack flexShrink={0}>
                    <Switch
                        colorScheme="orange" isChecked={filtros.sem_foto}
                        onChange={(e) => setFiltros((p) => ({ ...p, sem_foto: e.target.checked }))}
                    />
                    <Text fontSize="sm" whiteSpace="nowrap">Só sem visão lida</Text>
                </HStack>
                <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">{num(dado.total)} produtos</Text>
            </Stack>

            <Cartao overflow="hidden">
                {carregando
                    ? <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>
                    : <TabelaProdutos itens={dado.data} rotulos={dado.rotulos} aoMedir={setProdutoMedir} />}
            </Cartao>

            <ModalMedir produto={produtoMedir} aberto={!!produtoMedir} aoFechar={() => setProdutoMedir(null)} aoSalvar={buscar} />
        </VStack>
    );
};

const AbaSuspeitos = () => {
    const [dado, setDado] = useState({ data: [], total: 0 });
    const [tipo, setTipo] = useState('todos');
    const [carregando, setCarregando] = useState(false);
    const [produtoMedir, setProdutoMedir] = useState(null);

    const buscar = useCallback(async () => {
        setCarregando(true);
        try { setDado(await getDimensoesSuspeitos({ tipo, limite: 50 })); } finally { setCarregando(false); }
    }, [tipo]);

    useEffect(() => { buscar(); }, [buscar]);

    return (
        <VStack align="stretch" spacing={4}>
            <Cartao p={4} bg="orange.50" borderColor="orange.100">
                <Text fontSize="sm" color="gray.700">
                    Estimativas que a física do nosso próprio despacho refuta. Nenhuma das 510 caixas reais que já saíram
                    ficou abaixo de 0,14 kg, e peça de família pesada não pesa gramas. A fila vem ordenada por quantas
                    vezes o produto já vendeu.
                </Text>
            </Cartao>

            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align={{ sm: 'center' }}>
                <Select bg="white" borderRadius="lg" maxW={{ base: '100%', sm: '300px' }} value={tipo} onChange={(e) => setTipo(e.target.value)}>
                    <option value="todos">Todos os suspeitos</option>
                    <option value="abaixo_piso">Abaixo do piso físico (0,15 kg)</option>
                    <option value="familia_pesada">Peça pesada estimada leve</option>
                    <option value="discordancia">Texto e foto discordam</option>
                </Select>
                <Badge colorScheme="red" fontSize="sm" px={3} py={1.5} borderRadius="lg">
                    {num(dado.total)} produtos
                </Badge>
            </Stack>

            <Cartao overflow="hidden">
                {carregando
                    ? <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>
                    : <TabelaProdutos itens={dado.data} rotulos={{}} aoMedir={setProdutoMedir} mostrarVendas mostrarMotivo />}
            </Cartao>

            <ModalMedir produto={produtoMedir} aberto={!!produtoMedir} aoFechar={() => setProdutoMedir(null)} aoSalvar={buscar} />
        </VStack>
    );
};

const ModalFamilia = ({ familia, aberto, aoFechar, aoSalvar }) => {
    const [form, setForm] = useState({});
    const [previa, setPrevia] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const toast = useToast();

    useEffect(() => {
        setForm(familia || { nome: '', padrao: '', piso_kg: 0, peso_tipico_kg: '', fator_preco_minimo: 0.2, palavras_excluir: '', observacao: '', ativo: true });
        setPrevia(null);
    }, [familia]);

    const verPrevia = async () => {
        try {
            setPrevia(await previaDimensaoFamilia({
                padrao: form.padrao,
                piso_kg: form.piso_kg,
                fator_preco_minimo: form.fator_preco_minimo,
                palavras_excluir: form.palavras_excluir,
            }));
        } catch (err) {
            toast({ title: 'Falha na prévia', status: 'error', duration: 3000, isClosable: true });
        }
    };

    const salvar = async () => {
        setSalvando(true);
        try {
            await salvarDimensaoFamilia(form.id, form);
            toast({ title: 'Família salva', status: 'success', duration: 3000, isClosable: true });
            aoSalvar();
            aoFechar();
        } catch (err) {
            toast({
                title: 'Não foi possível salvar', description: err.response?.data?.error || err.message,
                status: 'error', duration: 5000, isClosable: true,
            });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal isOpen={aberto} onClose={aoFechar} size={{ base: 'full', md: 'xl' }} isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent borderRadius={{ base: 0, md: '2xl' }}>
                <ModalHeader fontSize="md">{form.id ? 'Editar família' : 'Nova família'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Nome<AjudaConfig titulo="Nome" texto={AJUDA_FAMILIA.nome} /></FormLabel>
                            <Input size="sm" borderRadius="lg" value={form.nome || ''} onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))} />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Padrão do nome (% é curinga)<AjudaConfig titulo="Padrão do nome" texto={AJUDA_FAMILIA.padrao} /></FormLabel>
                            <Input size="sm" borderRadius="lg" fontFamily="mono" value={form.padrao || ''} onChange={(e) => setForm((p) => ({ ...p, padrao: e.target.value }))} />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Piso de peso (kg)<AjudaConfig titulo="Piso de peso" texto={AJUDA_FAMILIA.piso} /></FormLabel>
                            <Input size="sm" borderRadius="lg" type="number" value={form.piso_kg ?? ''} onChange={(e) => setForm((p) => ({ ...p, piso_kg: e.target.value }))} />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Peso típico (kg)<AjudaConfig titulo="Peso típico" texto={AJUDA_FAMILIA.tipico} /></FormLabel>
                            <Input size="sm" borderRadius="lg" type="number" step="0.1" value={form.peso_tipico_kg ?? ''} onChange={(e) => setForm((p) => ({ ...p, peso_tipico_kg: e.target.value }))} />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                                Corte por preço (fração da mediana)
                                <AjudaConfig titulo="Corte por preço" texto={AJUDA_FAMILIA.corte} />
                            </FormLabel>
                            <Input size="sm" borderRadius="lg" type="number" step="0.05" value={form.fator_preco_minimo ?? ''} onChange={(e) => setForm((p) => ({ ...p, fator_preco_minimo: e.target.value }))} />
                        </FormControl>
                    </SimpleGrid>

                    <FormControl mt={4}>
                        <FormLabel fontSize="xs" mb={1}>Palavras que indicam acessório<AjudaConfig titulo="Palavras que indicam acessório" texto={AJUDA_FAMILIA.palavras} /></FormLabel>
                        <CampoPalavras
                            valor={form.palavras_excluir}
                            aoMudar={(v) => setForm((p) => ({ ...p, palavras_excluir: v }))}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                            Clique numa palavra para editar, no x para remover. Só valem nas duas primeiras palavras do
                            nome: BRAÇADEIRA FEIXE MOLA fica de fora, FEIXE MOLA SEM BUCHA continua dentro.
                        </Text>
                    </FormControl>

                    <FormControl mt={4}>
                        <FormLabel fontSize="xs" mb={1}>Observação<AjudaConfig titulo="Observação" texto={AJUDA_FAMILIA.observacao} /></FormLabel>
                        <Textarea size="sm" borderRadius="lg" rows={2} value={form.observacao || ''} onChange={(e) => setForm((p) => ({ ...p, observacao: e.target.value }))} />
                    </FormControl>

                    <Flex mt={4} align="center" gap={3} wrap="wrap">
                        <HStack>
                            <Switch colorScheme="orange" isChecked={!!form.ativo} onChange={(e) => setForm((p) => ({ ...p, ativo: e.target.checked }))} />
                            <Text fontSize="sm">Família ativa</Text>
                            <AjudaConfig titulo="Família ativa" texto={AJUDA_FAMILIA.ativa} />
                        </HStack>
                        <Button size="xs" ml="auto" variant="outline" borderRadius="lg" onClick={verPrevia}>Ver prévia</Button>
                    </Flex>

                    {previa && (
                        <Box mt={3} p={4} bg="gray.50" borderRadius="xl">
                            <Text fontSize="sm">
                                <b>{previa.produtos}</b> produtos casam com o padrão. Depois das peneiras, o piso
                                corrigiria <b>{previa.totais?.corrigidos ?? 0}</b>.
                            </Text>
                            {previa.corte_preco > 0 && (
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    Mediana de preço da família: {moeda(previa.preco_mediano)}. Abaixo de{' '}
                                    {moeda(previa.corte_preco)} o produto não herda o piso.
                                </Text>
                            )}

                            <Divider my={3} />

                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <GrupoPrevia
                                    cor="green" titulo="Herdam o piso" descricao="estimativa abaixo do piso hoje"
                                    total={previa.totais?.corrigidos} itens={previa.exemplos?.corrigidos}
                                />
                                <GrupoPrevia
                                    cor="gray" titulo="Já estão acima" descricao="o piso não muda nada neles"
                                    total={previa.totais?.ja_acima} itens={previa.exemplos?.ja_acima}
                                />
                                <GrupoPrevia
                                    cor="orange" titulo="Fora por palavra" descricao="acessório pelo nome"
                                    total={previa.totais?.fora_palavra} itens={previa.exemplos?.fora_palavra}
                                />
                                <GrupoPrevia
                                    cor="purple" titulo="Fora por preço" descricao="barato demais para ser a peça inteira"
                                    total={previa.totais?.fora_preco} itens={previa.exemplos?.fora_preco}
                                />
                            </SimpleGrid>
                        </Box>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button size="sm" variant="ghost" mr={2} onClick={aoFechar}>Cancelar</Button>
                    <Button size="sm" colorScheme="orange" borderRadius="lg" onClick={salvar} isLoading={salvando}>Salvar</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const ModalProdutosFamilia = ({ familia, aberto, aoFechar }) => {
    const [dado, setDado] = useState(null);
    const [busca, setBusca] = useState('');
    const toast = useToast();

    const buscar = useCallback(async () => {
        if (!familia) return;
        setDado(await getProdutosDaFamilia(familia.id, { busca, limite: 40 }));
    }, [familia, busca]);

    useEffect(() => { if (aberto) buscar(); }, [aberto, buscar]);

    const alternar = async (produtoId, incluido) => {
        await vincularProdutoFamilia(familia.id, { produto_id: produtoId, incluido });
        toast({
            title: incluido ? 'Produto incluído na família' : 'Produto tirado da família',
            status: 'success', duration: 2500, isClosable: true,
        });
        buscar();
    };

    return (
        <Modal isOpen={aberto} onClose={aoFechar} size={{ base: 'full', lg: '4xl' }} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent borderRadius={{ base: 0, md: '2xl' }}>
                <ModalHeader fontSize="md">Produtos de {familia?.nome}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Input
                        size="sm" borderRadius="lg" mb={4} placeholder="Filtrar por nome"
                        value={busca} onChange={(e) => setBusca(e.target.value)}
                    />
                    {!dado ? <Flex justify="center" py={8}><Spinner color="orange.500" /></Flex> : (
                        <>
                            {dado.fora?.length > 0 && (
                                <Box mb={4} p={3} bg="red.50" borderRadius="xl">
                                    <Text fontSize="xs" fontWeight="bold" mb={2}>Excluídos manualmente</Text>
                                    {dado.fora.map((p) => (
                                        <Flex key={p.id} fontSize="xs" py={1} align="center" gap={2}>
                                            <Text noOfLines={1} flex={1}>{p.nome}</Text>
                                            <Button size="xs" variant="link" colorScheme="blue" onClick={() => alternar(p.id, true)}>
                                                trazer de volta
                                            </Button>
                                        </Flex>
                                    ))}
                                </Box>
                            )}
                            <Rolavel>
                                <Table size="sm">
                                    <Thead bg="gray.50">
                                        <Tr><Th>Produto</Th><Th>Vínculo</Th><Th isNumeric>Estimativa</Th><Th></Th></Tr>
                                    </Thead>
                                    <Tbody>
                                        {dado.data.map((p) => (
                                            <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                                                <Td maxW="360px">
                                                    <Text fontSize="sm" noOfLines={1}>{p.nome}</Text>
                                                    <Badge fontSize="9px" fontFamily="mono">#{p.id}</Badge>
                                                </Td>
                                                <Td>
                                                    <Badge colorScheme={p.vinculo === 'manual' ? 'blue' : 'gray'} fontSize="9px">
                                                        {p.vinculo === 'manual' ? 'manual' : 'padrão'}
                                                    </Badge>
                                                </Td>
                                                <Td isNumeric fontSize="sm" whiteSpace="nowrap">{kg(p.cobravel_texto)}</Td>
                                                <Td textAlign="right">
                                                    <Button size="xs" variant="outline" colorScheme="red" borderRadius="lg" onClick={() => alternar(p.id, false)}>
                                                        tirar da família
                                                    </Button>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Rolavel>
                        </>
                    )}
                </ModalBody>
                <ModalFooter><Button size="sm" borderRadius="lg" onClick={aoFechar}>Fechar</Button></ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const AbaFamilias = () => {
    const [familias, setFamilias] = useState([]);
    const [filtros, setFiltros] = useState({ busca: '', situacao: 'todas', ordem: 'nome' });
    const [carregando, setCarregando] = useState(true);
    const [editando, setEditando] = useState(null);
    const [vendoProdutos, setVendoProdutos] = useState(null);

    const buscar = useCallback(async () => {
        setCarregando(true);
        try {
            const res = await getDimensaoFamilias(filtros);
            setFamilias(res.data || []);
        } finally { setCarregando(false); }
    }, [filtros]);

    useEffect(() => { buscar(); }, [buscar]);

    return (
        <VStack align="stretch" spacing={4}>
            <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={3} direction={{ base: 'column', md: 'row' }}>
                <Text fontSize="sm" color="gray.600" maxW="760px">
                    O piso impede que uma peça da família seja cotada como se fosse um parafuso. O corte por preço e as
                    palavras de acessório separam sozinhos o que só leva o nome da família.
                </Text>
                <Button size="sm" colorScheme="orange" borderRadius="lg" flexShrink={0} onClick={() => setEditando({})}>
                    Nova família
                </Button>
            </Flex>

            <Cartao p={{ base: 3, md: 4 }}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <InputGroup>
                        <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                        <Input
                            size="sm" borderRadius="lg" placeholder="Nome ou padrão" value={filtros.busca}
                            onChange={(e) => setFiltros((p) => ({ ...p, busca: e.target.value }))}
                        />
                    </InputGroup>
                    <Select
                        size="sm" borderRadius="lg" value={filtros.situacao}
                        onChange={(e) => setFiltros((p) => ({ ...p, situacao: e.target.value }))}
                    >
                        <option value="todas">Ativas e inativas</option>
                        <option value="ativas">Só ativas</option>
                        <option value="inativas">Só inativas</option>
                    </Select>
                    <Select
                        size="sm" borderRadius="lg" value={filtros.ordem}
                        onChange={(e) => setFiltros((p) => ({ ...p, ordem: e.target.value }))}
                    >
                        <option value="nome">Nome (A-Z)</option>
                        <option value="piso_desc">Maior piso</option>
                        <option value="piso_asc">Menor piso</option>
                        <option value="produtos_desc">Mais produtos</option>
                    </Select>
                </SimpleGrid>
            </Cartao>

            <Cartao overflow="hidden">
                {carregando ? <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex> : (
                    <Rolavel>
                        <Table size="sm">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>Família</Th><Th>Padrão</Th><Th isNumeric>Piso</Th><Th isNumeric>Peso típico</Th>
                                    <Th isNumeric>Produtos</Th>
                                    <Th isNumeric>
                                        <Flex align="center" justify="flex-end" gap={0}>
                                            Corrige
                                            <AjudaConfig titulo="Corrige" texto={AJUDA_FAMILIA.corrige} />
                                        </Flex>
                                    </Th>
                                    <Th isNumeric>Fora pelas peneiras</Th><Th>Preço mediano</Th><Th></Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {familias.map((f) => (
                                    <Tr key={f.id} opacity={f.ativo ? 1 : 0.5} _hover={{ bg: 'gray.50' }}>
                                        <Td>
                                            <Text fontWeight="medium" fontSize="sm" whiteSpace="nowrap">{f.nome}</Text>
                                            {!f.ativo && <Badge fontSize="9px">inativa</Badge>}
                                        </Td>
                                        <Td><Text fontSize="xs" fontFamily="mono" color="gray.600">{f.padrao}</Text></Td>
                                        <Td isNumeric fontWeight="bold" whiteSpace="nowrap">{Number(f.piso_kg).toFixed(1)} kg</Td>
                                        <Td isNumeric fontSize="sm" color="gray.600" whiteSpace="nowrap">
                                            {f.peso_tipico_kg ? `${Number(f.peso_tipico_kg).toFixed(1)} kg` : '-'}
                                        </Td>
                                        <Td isNumeric>{num(f.produtos)}</Td>
                                        <Td isNumeric>
                                            {f.abaixo_do_piso > 0
                                                ? <Badge colorScheme="red">{num(f.abaixo_do_piso)}</Badge>
                                                : <Text fontSize="sm" color="gray.400">0</Text>}
                                        </Td>
                                        <Td isNumeric fontSize="sm" color="gray.500">{num(f.fora_peneira || 0)}</Td>
                                        <Td fontSize="xs" whiteSpace="nowrap">
                                            {f.preco_mediano ? `R$ ${Number(f.preco_mediano).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                                        </Td>
                                        <Td textAlign="right">
                                            <HStack justify="flex-end" spacing={1}>
                                                <Button size="xs" variant="outline" borderRadius="lg" onClick={() => setVendoProdutos(f)}>Produtos</Button>
                                                <Button size="xs" colorScheme="orange" variant="outline" borderRadius="lg" onClick={() => setEditando(f)}>Editar</Button>
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Rolavel>
                )}
            </Cartao>

            <ModalFamilia familia={editando} aberto={!!editando} aoFechar={() => setEditando(null)} aoSalvar={buscar} />
            <ModalProdutosFamilia familia={vendoProdutos} aberto={!!vendoProdutos} aoFechar={() => setVendoProdutos(null)} />
        </VStack>
    );
};

const AbaLogs = () => {
    const [dado, setDado] = useState({ data: [], total: 0, fontes: [] });
    const [filtros, setFiltros] = useState({ busca: '', fonte: '', origem: 'todas', de: '', ate: '', ordem: 'recentes' });
    const [carregando, setCarregando] = useState(true);

    const buscar = useCallback(async () => {
        setCarregando(true);
        try { setDado(await getDimensoesLogs({ ...filtros, limite: 60 })); } finally { setCarregando(false); }
    }, [filtros]);

    useEffect(() => { buscar(); }, [buscar]);

    const set = (campo) => (e) => setFiltros((p) => ({ ...p, [campo]: e.target.value }));

    return (
        <VStack align="stretch" spacing={4}>
            <Cartao p={{ base: 3, md: 4 }}>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 6 }} spacing={3}>
                    <InputGroup>
                        <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                        <Input size="sm" borderRadius="lg" placeholder="Produto, SKU ou usuário" value={filtros.busca} onChange={set('busca')} />
                    </InputGroup>
                    <Select size="sm" borderRadius="lg" value={filtros.fonte} onChange={set('fonte')}>
                        <option value="">Todas as fontes</option>
                        {(dado.fontes || []).map((f) => <option key={f} value={f}>{f}</option>)}
                    </Select>
                    <Select size="sm" borderRadius="lg" value={filtros.origem} onChange={set('origem')}>
                        <option value="todas">Todas as origens</option>
                        <option value="0">Sem procedência</option>
                        <option value="1">Medido pelo Mercado Livre</option>
                        <option value="2">Caixa do despacho</option>
                        <option value="3">Medido pela TAGA</option>
                        <option value="4">Provisória (regra do fornecedor)</option>
                    </Select>
                    <Input size="sm" borderRadius="lg" type="date" value={filtros.de} onChange={set('de')} />
                    <Input size="sm" borderRadius="lg" type="date" value={filtros.ate} onChange={set('ate')} />
                    <Select size="sm" borderRadius="lg" value={filtros.ordem} onChange={set('ordem')}>
                        <option value="recentes">Mais recentes</option>
                        <option value="antigos">Mais antigos</option>
                        <option value="produto">Produto (A-Z)</option>
                        <option value="maior_peso">Maior peso gravado</option>
                    </Select>
                </SimpleGrid>
                <Flex mt={3} justify="space-between" align="center" gap={2} wrap="wrap">
                    <Text fontSize="sm" color="gray.500">{num(dado.total)} registros</Text>
                    <Button
                        size="xs" variant="ghost" borderRadius="lg"
                        onClick={() => setFiltros({ busca: '', fonte: '', origem: 'todas', de: '', ate: '', ordem: 'recentes' })}
                    >
                        Limpar filtros
                    </Button>
                </Flex>
            </Cartao>

            <Cartao overflow="hidden">
                {carregando ? <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex> : (
                    <Rolavel>
                        <Table size="sm">
                            <Thead bg="gray.50">
                                <Tr><Th>Quando</Th><Th>Produto</Th><Th>Origem</Th><Th>Antes</Th><Th>Depois</Th><Th>Fonte</Th><Th>Quem</Th></Tr>
                            </Thead>
                            <Tbody>
                                {dado.data.map((l) => (
                                    <Tr key={l.id} _hover={{ bg: 'gray.50' }}>
                                        <Td fontSize="xs" whiteSpace="nowrap">{dataHora(l.created_at)}</Td>
                                        <Td maxW="260px">
                                            <Text fontSize="sm" noOfLines={1}>{l.nome}</Text>
                                            <Badge fontSize="9px" fontFamily="mono">#{l.produto_id}</Badge>
                                        </Td>
                                        <Td>
                                            <HStack spacing={1}>
                                                <SeloOrigem origem={l.origem_anterior} rotulos={dado.rotulos} />
                                                <Text fontSize="xs">→</Text>
                                                <SeloOrigem origem={l.origem} rotulos={dado.rotulos} />
                                            </HStack>
                                        </Td>
                                        <Td fontSize="xs" color="gray.500" whiteSpace="nowrap">
                                            {l.altura_anterior}x{l.largura_anterior}x{l.comprimento_anterior} / {l.peso_anterior} kg
                                        </Td>
                                        <Td fontSize="xs" fontWeight="bold" whiteSpace="nowrap">
                                            {l.altura}x{l.largura}x{l.comprimento} / {l.peso} kg
                                        </Td>
                                        <Td><Tag size="sm" fontSize="10px">{l.fonte}</Tag></Td>
                                        <Td fontSize="xs" whiteSpace="nowrap">{l.user_name || '-'}</Td>
                                    </Tr>
                                ))}
                                {dado.data.length === 0 && (
                                    <Tr><Td colSpan={7}>
                                        <Text py={6} textAlign="center" color="gray.400" fontSize="sm">Nenhum registro para esse filtro</Text>
                                    </Td></Tr>
                                )}
                            </Tbody>
                        </Table>
                    </Rolavel>
                )}
            </Cartao>
        </VStack>
    );
};

const LinhaFicha = ({ rotulo, children }) => (
    <Flex py={2} gap={4} borderBottom="1px solid" borderColor="gray.100" align="flex-start" direction={{ base: 'column', sm: 'row' }}>
        <Text fontSize="xs" color="gray.500" minW={{ sm: '150px' }} flexShrink={0}>{rotulo}</Text>
        <Box fontSize="xs" color="gray.700" minW={0}>{children}</Box>
    </Flex>
);

const moeda = (v) => (v > 0 ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-');

// resultado do frete pode ser negativo, entao nao serve o moeda() que troca <=0 por travessao
const reais = (v) => (v == null ? '-' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

const GrupoPrevia = ({ cor, titulo, descricao, total, itens }) => (
    <Box borderWidth="1px" borderColor={`${cor}.200`} bg="white" borderRadius="lg" p={3}>
        <Flex align="baseline" gap={2}>
            <Badge colorScheme={cor} fontSize="10px">{total ?? 0}</Badge>
            <Text fontSize="xs" fontWeight="bold" color="gray.700">{titulo}</Text>
        </Flex>
        <Text fontSize="10px" color="gray.500" mb={2}>{descricao}</Text>
        {itens?.length ? (
            <VStack align="stretch" spacing={1}>
                {itens.map((it, i) => (
                    <Box key={i}>
                        <Text fontSize="xs" color="gray.700" noOfLines={1}>{it.nome}</Text>
                        <Text fontSize="10px" color="gray.500">
                            {moeda(it.preco)}
                            {it.estimativa != null && ` · estima ${Number(it.estimativa).toFixed(2)} kg`}
                            {it.palavra && ` · casou "${it.palavra}"`}
                        </Text>
                    </Box>
                ))}
            </VStack>
        ) : (
            <Text fontSize="xs" color="gray.400">nenhum</Text>
        )}
    </Box>
);

const CampoPalavras = ({ valor, aoMudar }) => {
    const [texto, setTexto] = useState('');
    const lista = String(valor || '').split(',').map((p) => p.trim()).filter(Boolean);

    const aplicar = (nova) => aoMudar(nova.join(','));

    const adicionar = () => {
        const limpo = texto.trim().toUpperCase().replace(/,/g, '');
        setTexto('');
        if (!limpo || lista.some((p) => p.toUpperCase() === limpo)) return;
        aplicar([...lista, limpo]);
    };

    const editar = (i) => {
        setTexto(lista[i]);
        aplicar(lista.filter((_, x) => x !== i));
    };

    const aoTeclar = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            adicionar();
        } else if (e.key === 'Backspace' && !texto && lista.length) {
            editar(lista.length - 1);
        }
    };

    return (
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" px={2} py={2}>
            {lista.length > 0 && (
                <Wrap spacing={1.5} mb={2}>
                    {lista.map((palavra, i) => (
                        <WrapItem key={palavra}>
                            <Tag size="sm" borderRadius="md" colorScheme="orange" variant="subtle">
                                <TagLabel
                                    cursor="pointer"
                                    title="Clique para editar"
                                    onClick={() => editar(i)}
                                >
                                    {palavra}
                                </TagLabel>
                                <TagCloseButton
                                    aria-label={'Remover ' + palavra}
                                    onClick={() => aplicar(lista.filter((_, x) => x !== i))}
                                />
                            </Tag>
                        </WrapItem>
                    ))}
                </Wrap>
            )}
            <Input
                size="sm"
                variant="unstyled"
                px={1}
                placeholder={lista.length ? 'Digite e tecle Enter' : 'Ex.: BRACADEIRA, e tecle Enter'}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={aoTeclar}
                onBlur={adicionar}
            />
        </Box>
    );
};

const AJUDA_FAMILIA = {
    nome: 'Só um rótulo para você reconhecer a família na listagem. Não entra em cálculo nenhum.',
    padrao: 'É o campo que faz o trabalho: uma busca no nome de todos os produtos, onde % significa "qualquer coisa aqui". '
        + 'FEIXE MOLA% pega tudo que começa com FEIXE MOLA. %VIRABREQUIM% pega tudo que contém VIRABREQUIM em qualquer posição. '
        + 'Padrão largo demais é o erro mais comum, por isso use o botão Ver prévia antes de salvar: ele mostra quantos produtos seriam alcançados.',
    piso: 'O peso mínimo da família, em kg. Nenhum produto alcançado sai abaixo disso. '
        + 'Só age para cima: se a estimativa já for maior que o piso, nada muda. '
        + 'E só tem efeito se for maior que o piso global de 0,15 kg, senão a família é ignorada. '
        + 'Existe porque o modelo de texto subestima peça pesada de aço: sem o piso, ele chega a cotar feixe de mola como 8 kg.',
    corte: 'A primeira peneira contra acessório, de 0 a 1. O sistema calcula sozinho a mediana do preço de venda da família, '
        + 'e o produto que custar menos que mediana vezes esse fator não herda o piso. '
        + 'Com 0,2 e mediana de R$ 900, tudo abaixo de R$ 180 fica de fora. '
        + 'A lógica: o feixe de mola custa centenas de reais e o grampo que prende o feixe custa dezenas, então o preço denuncia que não é a peça inteira. '
        + 'Deixe 0 para desligar essa peneira.',
    palavras: 'A segunda peneira contra acessório, separada por vírgula. A palavra só desqualifica quando aparece nas duas primeiras palavras do nome, '
        + 'que é onde fica o núcleo do que a peça é. Assim BRAÇADEIRA FEIXE MOLA fica de fora por ser braçadeira, '
        + 'mas FEIXE MOLA 1 LAMINA SEM BUCHA continua dentro, mesmo tendo BUCHA no nome, porque a palavra está no fim.',
    corrige: 'Quantos produtos da família a estimativa de hoje coloca abaixo do piso, já descontando os que as peneiras de preço e de palavra descartam. '
        + 'É o trabalho que a família está fazendo agora: família com muitos produtos e zero aqui não está corrigindo nada. '
        + 'Esse número não é uma fila a resolver e nunca vai zerar sozinho: o piso é aplicado na hora da cotação e não regrava a previsão do texto no banco, '
        + 'então a contagem continua medindo a previsão crua. '
        + 'Vale lembrar que o corrigido sai com o piso exato, todos no mesmo valor, e não com uma estimativa individual.',
    tipico: 'Quanto a peça pesa de verdade, na média. É só referência para você calibrar o piso ao lado, não entra em cálculo nenhum. '
        + 'Repare que o piso é sempre bem menor que o peso típico, de propósito: o feixe de mola pesa 56 kg em média e o piso é 10 kg, '
        + 'porque existe feixe de 1 lâmina que pesa pouco e um piso de 56 estragaria a estimativa dele. O piso não é um chute do peso, é um limite do absurdo.',
    observacao: 'Texto livre para registrar de onde veio o número e por que a família existe. As famílias que já vieram cadastradas trazem a data e a fonte do levantamento.',
    ativa: 'Desligar suspende a família sem apagar o cadastro. Família desligada não levanta o piso de ninguém.',
    manual: 'A inclusão e a exclusão manual de produto vencem tudo: se um produto foi marcado à mão, nem o padrão do nome nem as peneiras de preço e palavra são consultados para ele.',
    ordem: 'A ordem em que a família decide: primeiro a exceção manual, que vence tudo. Não havendo, o nome precisa casar com o padrão. '
        + 'Casando, o produto ainda pode ser descartado pelas peneiras de preço ou de palavra. '
        + 'E se o produto for alcançado por duas famílias, vale o piso mais alto.',
};

const AJUDA_CAIXA = 'A caixa não é uma medição da peça: é uma embalagem deduzida do peso que o modelo já escolheu. '
    + 'Exemplo com 5 kg cobráveis: o volume alvo é 5 vezes 6.000, ou seja 30.000 cm³. A foto viu 10 x 20 x 60 cm, o que dá a proporção 1 para 2 para 6. '
    + 'Multiplicando os três lados pelo mesmo fator (1,357) sai 13,6 x 27,1 x 81,4 cm, que fecha 30.000 cm³ e cubado de exatos 5 kg. '
    + 'Se fossem 15 kg, a mesma proporção pediria 117 cm de comprimento: aí o comprimento trava em 100 cm e os outros dois crescem para recompor o volume, virando 21,2 x 42,4 x 100 cm, ainda com 15 kg. '
    + 'Sem foto, os mesmos 30.000 cm³ viram um cubo de 31,1 cm de lado. '
    + 'O motivo de fixar o cubado no peso cobrável é que a transportadora cobra o maior entre peso real e cubado: igualando os dois, tanto faz qual ela usar.';

const CorTrilho = 'gray.200';

const Decisao = ({ ordem, titulo, descricao, saida, ultimo, entra }) => (
    <Flex align="stretch" gap={0}>
        <Flex direction="column" align="center" flexShrink={0} w="34px">
            <Flex
                bg="white" border="2px solid" borderColor="orange.400" color="orange.500"
                w="26px" h="26px" borderRadius="full" align="center" justify="center"
                fontWeight="bold" fontSize="xs" flexShrink={0} zIndex={1}
            >
                {ordem}
            </Flex>
            {!ultimo && <Box flex={1} w="2px" bg={CorTrilho} minH="18px" />}
        </Flex>

        <Box flex={1} pb={5} minW={0}>
            <Flex
                border="1px solid" borderColor="orange.200" bg="orange.50" borderRadius="xl"
                px={3} py={2.5} direction="column"
            >
                <Text fontSize="sm" fontWeight="bold" color="gray.800">{titulo}</Text>
                <Text fontSize="xs" color="gray.600" mt={0.5}>{descricao}</Text>
                {entra && <EntraSai entra={entra} />}
            </Flex>

            <Flex align="center" gap={2} mt={2} ml={{ base: 2, md: 6 }}>
                <Box w={{ base: '14px', md: '24px' }} h="2px" bg="green.300" flexShrink={0} />
                <Badge colorScheme="green" fontSize="9px" flexShrink={0}>SIM</Badge>
                <Flex
                    border="1px solid" borderColor="green.200" bg="green.50" borderRadius="lg"
                    px={3} py={1.5} minW={0} flex={1}
                >
                    <Text fontSize="xs" color="green.800" fontWeight="medium">{saida}</Text>
                </Flex>
            </Flex>
            <Flex align="center" gap={2} mt={1.5} ml={{ base: 2, md: 6 }}>
                <Box w={{ base: '14px', md: '24px' }} h="2px" bg={CorTrilho} flexShrink={0} />
                <Badge colorScheme="gray" fontSize="9px" flexShrink={0}>NÃO</Badge>
                <Text fontSize="xs" color="gray.500">desce para a próxima etapa</Text>
            </Flex>
        </Box>
    </Flex>
);

const EntraSai = ({ entra, sai }) => (
    <Box mt={2} borderTop="1px solid" borderColor="blackAlpha.100" pt={2}>
        {entra && (
            <Flex gap={2} align="baseline">
                <Text fontSize="10px" fontWeight="bold" color="gray.400" flexShrink={0} w="34px">ENTRA</Text>
                <Text fontSize="xs" color="gray.700" minW={0}>{entra}</Text>
            </Flex>
        )}
        {sai && (
            <Flex gap={2} align="baseline" mt={1}>
                <Text fontSize="10px" fontWeight="bold" color="gray.400" flexShrink={0} w="34px">SAI</Text>
                <Text fontSize="xs" color="gray.700" minW={0}>{sai}</Text>
            </Flex>
        )}
    </Box>
);

const Etapa = ({ ordem, titulo, descricao, cor = 'blue', ultimo, destaque, ajuda, entra, sai }) => (
    <Flex align="stretch" gap={0}>
        <Flex direction="column" align="center" flexShrink={0} w="34px">
            <Flex
                bg={`${cor}.500`} color="white" w="26px" h="26px" borderRadius="full"
                align="center" justify="center" fontWeight="bold" fontSize="xs" flexShrink={0} zIndex={1}
            >
                {ordem}
            </Flex>
            {!ultimo && <Box flex={1} w="2px" bg={CorTrilho} minH="18px" />}
        </Flex>

        <Box flex={1} pb={5} minW={0}>
            <Flex
                border="1px solid" borderColor={destaque ? `${cor}.200` : 'gray.200'}
                bg={destaque ? `${cor}.50` : 'white'} borderRadius="xl" px={3} py={2.5} direction="column"
            >
                <Flex align="center" gap={0}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.800">{titulo}</Text>
                    {ajuda && <AjudaConfig titulo={titulo} texto={ajuda} />}
                </Flex>
                <Text fontSize="xs" color="gray.600" mt={0.5}>{descricao}</Text>
                {(entra || sai) && <EntraSai entra={entra} sai={sai} />}
            </Flex>
        </Box>
    </Flex>
);

const Fluxograma = ({ cfg }) => {
    const valor = (chave) => cfg?.find((c) => c.chave === chave)?.valor;
    const preserva = Number(valor('dim_preservar_formato')) >= 1;

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={1} gap={2} wrap="wrap">
                <Text fontSize="sm" fontWeight="bold">Como o estimador decide</Text>
                <HStack spacing={3} fontSize="10px" color="gray.500">
                    <HStack spacing={1}><Box w="8px" h="8px" borderRadius="full" border="2px solid" borderColor="orange.400" />
                        <Text>decisão</Text></HStack>
                    <HStack spacing={1}><Box w="8px" h="8px" borderRadius="full" bg="blue.500" /><Text>processamento</Text></HStack>
                </HStack>
            </Flex>
            <Text fontSize="xs" color="gray.500" mb={5}>
                Entra o código de um produto, sai altura, largura, comprimento e peso. Tudo no meio serve para
                chegar nesses quatro números. A cascata para na primeira etapa que resolve.
            </Text>

            <Decisao
                ordem="1" titulo="O próprio cadastro já tem medida confiável?"
                entra="O produto."
                descricao="Antes de estimar, confere se alguém já mediu essa peça de verdade. Exige as duas coisas: origem confiável (medido pelo ML, caixa do despacho ou medido pela TAGA) e as quatro medidas preenchidas. Com qualquer medida zerada, não conta. A provisória da regra BL PARTS também vale aqui, mas só para o próprio produto: ela é inflada de propósito e não serve de gêmeo para outra peça."
                saida="Devolve a medida do cadastro e não estima nada"
            />
            <Decisao
                ordem="2" titulo="Existe gêmeo pelo código global?"
                entra="O código de fabricante da peça."
                descricao="Procura no catálogo outra peça com o mesmo código de fabricante que já tenha medida confiável. Se a mesma peça está cadastrada duas vezes, uma resolve a outra."
                saida="Devolve a medida do gêmeo"
            />
            <Etapa
                ordem="3" cor="blue" titulo="Perna de texto"
                entra="Nome, descrição, preço, fabricante e NCM."
                sai="Um número só, em kg. Sem lados, sem formato."
                descricao="O modelo tem uma tabela com um peso aprendido para cada palavra do catálogo, monta a soma das palavras da peça e devolve o resultado. Roda antes, para o catálogo inteiro, e aqui só é lido do banco. Produto sem previsão gravada fica sem essa perna."
            />
            <Etapa
                ordem="4" cor="blue" titulo="Perna de visão"
                entra="A foto do produto."
                sai="Um número em kg, vindo de quatro (altura, largura, comprimento e peso)."
                descricao={'A IA olha a foto e responde como despacharíamos a peça, devolvendo caixa e peso. Desses quatro números sai um cobrável só, multiplicado por ' + (valor('dim_fator_embalagem_visao') || '1.5') + ' porque a foto mostra a peça nua e o que cotamos é a caixa fechada. A primeira leitura fica em cache. Produto sem foto fica sem essa perna.'}
            />
            <Decisao
                ordem="5" titulo="Ficou sem as duas pernas?"
                entra="As duas pernas."
                descricao="Nem previsão de texto, nem foto lida. Sem nenhuma informação sobre a peça, resta o chute conservador."
                saida={'Caixa padrão de ' + (valor('dim_fallback_lado_cm') || '20') + ' cm por lado e ' + (valor('dim_fallback_peso_kg') || '2') + ' kg, marcada como fallback'}
            />
            <Etapa
                ordem="6" cor="blue" titulo="Combinação das pernas"
                entra="Os dois números das etapas 3 e 4."
                sai="Um número em kg. É aqui que o peso é decidido."
                descricao="Média geométrica entre os dois, ou seja a raiz quadrada da multiplicação. É usada no lugar da média comum porque, com valores distantes, a comum seria puxada demais pelo maior. Se só uma das pernas existir, ela vira o resultado direto, sem combinação."
            />
            <Etapa
                ordem="7" cor="purple" destaque titulo="Piso de sanidade"
                entra="O peso decidido na etapa 6."
                sai="O mesmo peso, ou o piso, se o peso estiver abaixo dele."
                descricao={'Não muda a estimativa, só barra o absurdo. Nada sai abaixo de ' + String(valor('dim_piso_global_kg') ?? '0,15').replace('.', ',') + ' kg, e peça de família pesada respeita o piso da família, valendo sempre o maior piso que alcança o produto. É o que impede um bloco de motor ser cotado como 36 gramas.'}
            />
            <Etapa
                ordem="8" cor="green" destaque ultimo titulo="Montagem da caixa"
                ajuda={AJUDA_CAIXA}
                entra="O peso final e, quando existe foto, a proporção entre os lados que ela enxergou."
                sai="Altura, largura, comprimento e peso: a resposta da cotação." 
                descricao={preserva
                    ? 'A cotação exige três lados, não só um peso, então a caixa é montada de trás para frente: o volume é fixado em peso cobrável vezes 6.000, para o cubado dar exatamente o peso já decidido. A foto entra só com a proporção entre os lados, nunca com o tamanho. Lado acima de ' + (valor('dim_lado_max_cm') || '100') + ' cm trava no teto e a sobra vai para os outros dois, sem mudar o peso. Sem foto não há proporção a copiar, e sai um cubo.'
                    : 'A cotação exige três lados, não só um peso, então a caixa é montada de trás para frente: o volume é fixado em peso cobrável vezes 6.000, para o cubado dar exatamente o peso já decidido. Com a preservação de formato desligada, a caixa sai sempre como um cubo, mesmo quando a foto mostrou uma peça comprida.'}
            />

            <Box mt={2} p={3} bg="gray.50" borderRadius="xl">
                <Text fontSize="10px" color="gray.500" fontWeight="bold" mb={1}>EXEMPLO REAL, SKU 72618</Text>
                <Text fontSize="xs" color="gray.600">
                    Texto previu 8,345 kg e a foto, já com o fator de embalagem, 37,5 kg. A média geométrica deu
                    17,69 kg. A foto tinha enxergado 10 x 20 x 120 cm, e essa proporção esticada até bater os
                    17,69 kg passava de 100 cm, então o maior lado ficou no teto e a sobra foi para os outros
                    dois: <b>23 x 46,1 x 100 cm</b>.
                </Text>
            </Box>
        </Box>
    );
};

const AjudaConfig = ({ titulo, texto }) => (
    <Popover placement="auto" trigger="hover" openDelay={150}>
        <PopoverTrigger>
            <IconButton
                aria-label={'Como funciona: ' + titulo} icon={<QuestionOutlineIcon />}
                size="xs" variant="ghost" colorScheme="gray" borderRadius="full" ml={1}
                onClick={(e) => e.preventDefault()}
            />
        </PopoverTrigger>
        <PopoverContent maxW="420px" boxShadow="lg" borderRadius="xl">
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader fontSize="sm" fontWeight="bold" border="none" pb={1}>{titulo}</PopoverHeader>
            <PopoverBody fontSize="xs" color="gray.600" pt={0} pb={4} lineHeight="1.6">
                {texto}
            </PopoverBody>
        </PopoverContent>
    </Popover>
);

// Mesma conta do backend (DimensoesBlpartsService): acréscimo, arredonda meio para
// cima e só depois o piso. Em inteiros, porque 59 x 1,15 em ponto flutuante dá 67,8499...
const comAcrescimo = (valor, pct, casas) => {
    const bruto = Math.round(valor * 1000) * (10000 + Math.round(pct * 100));
    const passo = 10 ** (7 - casas);
    return Math.floor((bruto + passo / 2) / passo) / 10 ** casas;
};

const decimalBr = (v, casas = 3) => Number(v).toLocaleString('pt-BR', { maximumFractionDigits: casas });

// Exemplo do documento da regra: o 311250, que a BL informa com 18 x 59 x 71 cm e 3,750 kg.
const exemploRegraBl = (pct, piso, divisor) => {
    if ([pct, piso, divisor].some((v) => Number.isNaN(v)) || divisor <= 0) return null;
    const [a, l, c] = [18, 59, 71].map((v) => Math.max(comAcrescimo(v, pct, 1), piso));
    return {
        caixa: `${decimalBr(a, 1)} × ${decimalBr(l, 1)} × ${decimalBr(c, 1)}`,
        peso: decimalBr(comAcrescimo(3.75, pct, 3)),
        cubado: decimalBr((a * l * c) / divisor, 2),
    };
};

const CartaoRegraBl = ({ itens, aoSalvar }) => {
    const toast = useToast();
    const [valores, setValores] = useState({});
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        setValores(Object.fromEntries(itens.map((c) => [c.chave, String(c.valor)])));
    }, [itens]);

    const invalido = (c) => {
        const v = valores[c.chave];
        return v === undefined || v === '' || Number.isNaN(Number(v)) || Number(v) < c.min || Number(v) > c.max;
    };
    const temInvalido = itens.some(invalido);
    const mudou = itens.some((c) => Number(valores[c.chave]) !== Number(c.valor));
    const exemplo = exemploRegraBl(
        Number(valores.dim_bl_acrescimo_pct), Number(valores.dim_bl_piso_cm), Number(valores.dim_bl_divisor_cubagem),
    );

    const salvar = async () => {
        setSalvando(true);
        try {
            await salvarDimensaoConfig(Object.fromEntries(itens.map((c) => [c.chave, Number(valores[c.chave])])));
            toast({
                title: 'Regra BL PARTS salva', description: 'Vale para a próxima carga de medida do fornecedor.',
                status: 'success', duration: 4000, isClosable: true,
            });
            aoSalvar();
        } catch (err) {
            toast({
                title: 'Não foi possível salvar', description: err.response?.data?.error || err.message,
                status: 'error', duration: 5000, isClosable: true,
            });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Cartao p={{ base: 4, md: 5 }}>
            <Text fontSize="sm" fontWeight="bold">Regra BL PARTS</Text>
            <Text fontSize="xs" color="gray.500" mt={1} mb={4}>
                A medida do fornecedor entra com acréscimo e piso, como provisória, até a peça ser medida no estoque.
                Vale para a próxima carga: o que já está gravado não é recalculado.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {itens.map((c) => (
                    <FormControl key={c.chave} isInvalid={invalido(c)}>
                        <Flex align="center" mb={1}>
                            <FormLabel fontSize="xs" fontWeight="medium" mb={0} mr={0}>{c.rotulo}</FormLabel>
                            {c.detalhe && <AjudaConfig titulo={c.rotulo} texto={c.detalhe} />}
                        </Flex>
                        <Input
                            size="sm" type="number" inputMode="decimal" borderRadius="lg" bg="white"
                            min={c.min} max={c.max} step={c.passo} value={valores[c.chave] ?? ''}
                            onChange={(e) => setValores((p) => ({ ...p, [c.chave]: e.target.value }))}
                        />
                        <Text fontSize="10px" color={invalido(c) ? 'red.500' : 'gray.500'} mt={1}>
                            {invalido(c) ? `Use um número entre ${decimalBr(c.min)} e ${decimalBr(c.max)}.` : c.ajuda}
                        </Text>
                        {c.alterado && <Badge mt={1} colorScheme="yellow" fontSize="9px">padrão {c.padrao_fabrica}</Badge>}
                    </FormControl>
                ))}
            </SimpleGrid>
            {exemplo && (
                <Box mt={4} p={3} bg="orange.50" borderRadius="lg">
                    <Text fontSize="xs" color="gray.700">
                        Exemplo: a BL informa o 311250 com 18 × 59 × 71 cm e 3,75 kg. Com estes valores ele entra com{' '}
                        <b>{exemplo.caixa} cm</b> e <b>{exemplo.peso} kg</b>, peso cubado de <b>{exemplo.cubado} kg</b>.
                    </Text>
                </Box>
            )}
            <Flex justify="flex-end" mt={4}>
                <Button
                    size="sm" colorScheme="orange" borderRadius="lg" w={{ base: '100%', md: 'auto' }}
                    onClick={salvar} isLoading={salvando} isDisabled={!mudou || temInvalido}
                >
                    Salvar regra
                </Button>
            </Flex>
        </Cartao>
    );
};

const AbaConfiguracoes = () => {
    const [cfg, setCfg] = useState(null);

    const carregar = useCallback(() => getDimensaoConfig().then((res) => setCfg(res.data)), []);

    useEffect(() => { carregar(); }, [carregar]);

    if (!cfg) return <Flex justify="center" py={12}><Spinner color="orange.500" /></Flex>;

    const modelo = cfg.filter((c) => c.grupo !== 'regra_bl');
    const regraBl = cfg.filter((c) => c.grupo === 'regra_bl');

    return (
        <VStack align="stretch" spacing={4}>
            {regraBl.length > 0 && <CartaoRegraBl itens={regraBl} aoSalvar={carregar} />}

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4} alignItems="flex-start">
                <Cartao p={{ base: 4, md: 5 }}>
                    <Text fontSize="sm" fontWeight="bold" mb={4}>Parâmetros do modelo</Text>
                    <VStack align="stretch" spacing={4} divider={<Divider />}>
                        {modelo.map((c) => (
                            <Flex key={c.chave} justify="space-between" align="flex-start" gap={4}>
                                <Box flex={1} minW={0}>
                                    <Flex align="center" gap={0}>
                                        <Text fontSize="sm" fontWeight="medium">{c.rotulo}</Text>
                                        {c.detalhe && <AjudaConfig titulo={c.rotulo} texto={c.detalhe} />}
                                    </Flex>
                                    <Text fontSize="xs" color="gray.500" mt={0.5}>{c.ajuda}</Text>
                                    <Text fontSize="10px" color="gray.400" mt={1} fontFamily="mono">{c.chave}</Text>
                                </Box>
                                <Box flexShrink={0} textAlign="right" pt={1}>
                                    <Text fontSize="lg" fontWeight="bold" color="gray.800" lineHeight="1.2">
                                        {c.tipo === 'booleano'
                                            ? (Number(c.valor) >= 1 ? 'Ligado' : 'Desligado')
                                            : Number(c.valor).toLocaleString('pt-BR')}
                                    </Text>
                                    {c.alterado && <Badge mt={1} colorScheme="yellow" fontSize="9px">padrão {c.padrao_fabrica}</Badge>}
                                </Box>
                            </Flex>
                        ))}
                    </VStack>
                </Cartao>

                <Cartao p={{ base: 4, md: 5 }}>
                    <Fluxograma cfg={modelo} />
                </Cartao>
            </SimpleGrid>
        </VStack>
    );
};

const FAIXAS_RAZAO = [
    { chave: 'muito_menor', rotulo: 'Novo cota menos da metade', cor: 'teal' },
    { chave: 'menor', rotulo: 'Novo cota menos', cor: 'teal' },
    { chave: 'igual', rotulo: 'Praticamente iguais', cor: 'gray' },
    { chave: 'maior', rotulo: 'Novo cota mais', cor: 'blue' },
    { chave: 'muito_maior', rotulo: 'Novo cota mais que o dobro', cor: 'blue' },
];

const CaixaEstimativa = ({ a, l, c, peso, cobravel, metodo }) => (
    <Box minW={0}>
        <Text fontSize="sm" fontWeight="bold" whiteSpace="nowrap">{Number(cobravel).toFixed(2)} kg</Text>
        <Text fontSize="10px" color="gray.500" whiteSpace="nowrap">
            {Number(a).toFixed(0)}x{Number(l).toFixed(0)}x{Number(c).toFixed(0)} cm, {Number(peso).toFixed(2)} kg
        </Text>
        <Text fontSize="10px" color="gray.400" fontFamily="mono" noOfLines={1}>{metodo}</Text>
    </Box>
);

const SeloRazao = ({ razao }) => {
    if (!razao) return <Text fontSize="xs" color="gray.400">-</Text>;
    const r = Number(razao);
    const cor = r > 1.05 ? 'blue' : r < 0.95 ? 'teal' : 'gray';
    const texto = r > 1.05 ? `+${((r - 1) * 100).toFixed(0)}%` : r < 0.95 ? `-${((1 - r) * 100).toFixed(0)}%` : 'igual';
    return <Badge colorScheme={cor} fontSize="10px" borderRadius="md">{texto}</Badge>;
};

const AbaCotacoes = () => {
    const [dado, setDado] = useState({ data: [], total: 0, resumo: {}, contextos: [], metodos: [] });
    const [filtros, setFiltros] = useState({ busca: '', contexto: '', metodo: '', direcao: 'todas', de: '', ate: '', ordem: 'recentes' });
    const [carregando, setCarregando] = useState(true);

    const buscar = useCallback(async () => {
        setCarregando(true);
        try { setDado(await getDimensoesCotacoes({ ...filtros, limite: 60 })); } finally { setCarregando(false); }
    }, [filtros]);

    useEffect(() => { buscar(); }, [buscar]);

    const set = (campo) => (e) => setFiltros((p) => ({ ...p, [campo]: e.target.value }));
    const r = dado.resumo || {};
    const faixas = r.faixas || {};
    const totalFaixas = Object.values(faixas).reduce((a, b) => a + Number(b || 0), 0) || 1;

    return (
        <VStack align="stretch" spacing={4}>
            <Cartao p={{ base: 4, md: 5 }}>
                <Text fontSize="sm" fontWeight="bold">O estimador antigo contra o novo, na prática</Text>
                <Text fontSize="xs" color="gray.500" mt={0.5} mb={4}>
                    O novo (v2) responde as cotações desde 19/08. O antigo continua sendo calculado a cada
                    cotação, só para registro. Nenhuma das duas colunas é a resposta certa: só a caixa que
                    realmente sai resolve. Aqui é só a diferença entre eles.
                </Text>
                <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={3}>
                    <Indicador rotulo="Comparações" valor={num(r.amostras)} ajuda={`${num(r.produtos)} produtos distintos`} />
                    <Indicador
                        rotulo="Diferença mediana" valor={r.mediana ? `${Number(r.mediana).toFixed(2)}x` : '-'}
                        ajuda={r.mediana > 1 ? 'o novo cota mais' : r.mediana < 1 ? 'o novo cota menos' : 'empatados'}
                    />
                    <Indicador rotulo="Quilos a mais" valor={`${num(r.kg_a_mais)} kg`} cor="blue.500" ajuda="somados, onde o novo cota mais" />
                    <Indicador rotulo="Quilos a menos" valor={`${num(r.kg_a_menos)} kg`} cor="teal.500" ajuda="somados, onde o novo cota menos" />
                </SimpleGrid>

                {r.amostras > 0 && (
                    <Box mt={5}>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Como as diferenças se distribuem</Text>
                        <VStack align="stretch" spacing={2}>
                            {FAIXAS_RAZAO.map((f) => (
                                <Box key={f.chave}>
                                    <Flex justify="space-between" fontSize="xs" mb={1} gap={2}>
                                        <Text color="gray.600">{f.rotulo}</Text>
                                        <Text fontWeight="medium" whiteSpace="nowrap">
                                            {num(faixas[f.chave])} ({Math.round((100 * Number(faixas[f.chave] || 0)) / totalFaixas)}%)
                                        </Text>
                                    </Flex>
                                    <Progress
                                        value={(100 * Number(faixas[f.chave] || 0)) / totalFaixas}
                                        size="xs" borderRadius="full" colorScheme={f.cor}
                                    />
                                </Box>
                            ))}
                        </VStack>
                    </Box>
                )}
            </Cartao>

            <Cartao p={{ base: 3, md: 4 }}>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 6 }} spacing={3}>
                    <InputGroup>
                        <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                        <Input size="sm" borderRadius="lg" placeholder="Produto ou SKU" value={filtros.busca} onChange={set('busca')} />
                    </InputGroup>
                    <Select size="sm" borderRadius="lg" value={filtros.direcao} onChange={set('direcao')}>
                        <option value="todas">Todas as diferenças</option>
                        <option value="divergente">Diferença acima de 2x</option>
                        <option value="maior">Novo cota mais</option>
                        <option value="menor">Novo cota menos</option>
                        <option value="igual">Praticamente iguais</option>
                    </Select>
                    <Select size="sm" borderRadius="lg" value={filtros.contexto} onChange={set('contexto')}>
                        <option value="">Todos os contextos</option>
                        {(dado.contextos || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </Select>
                    <Select size="sm" borderRadius="lg" value={filtros.metodo} onChange={set('metodo')}>
                        <option value="">Todos os métodos do novo</option>
                        {(dado.metodos || []).map((m) => <option key={m} value={m}>{m}</option>)}
                    </Select>
                    <Input size="sm" borderRadius="lg" type="date" value={filtros.de} onChange={set('de')} />
                    <Select size="sm" borderRadius="lg" value={filtros.ordem} onChange={set('ordem')}>
                        <option value="recentes">Mais recentes</option>
                        <option value="antigos">Mais antigos</option>
                        <option value="maior_razao">Maior diferença para cima</option>
                        <option value="menor_razao">Maior diferença para baixo</option>
                    </Select>
                </SimpleGrid>
                <Flex mt={3} justify="space-between" align="center" gap={2} wrap="wrap">
                    <Text fontSize="sm" color="gray.500">{num(dado.total)} registros</Text>
                    <Button
                        size="xs" variant="ghost" borderRadius="lg"
                        onClick={() => setFiltros({ busca: '', contexto: '', metodo: '', direcao: 'todas', de: '', ate: '', ordem: 'recentes' })}
                    >
                        Limpar filtros
                    </Button>
                </Flex>
            </Cartao>

            <Cartao overflow="hidden">
                {carregando ? <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex> : (
                    <Rolavel>
                        <Table size="sm">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>Quando</Th><Th>Produto</Th><Th>Antigo</Th><Th>Novo</Th>
                                    <Th isNumeric>Diferença</Th><Th>Pernas do novo</Th><Th>Contexto</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {dado.data.map((l) => (
                                    <Tr key={l.id} _hover={{ bg: 'gray.50' }}>
                                        <Td fontSize="xs" whiteSpace="nowrap">{dataHora(l.created_at)}</Td>
                                        <Td maxW="240px">
                                            <Text fontSize="sm" noOfLines={1}>{l.nome}</Text>
                                            <Badge fontSize="9px" fontFamily="mono">#{l.produto_id}</Badge>
                                        </Td>
                                        <Td>
                                            <CaixaEstimativa
                                                a={l.antigo_altura} l={l.antigo_largura} c={l.antigo_comprimento}
                                                peso={l.antigo_peso} cobravel={l.antigo_cobravel} metodo={l.antigo_metodo}
                                            />
                                        </Td>
                                        <Td>
                                            <CaixaEstimativa
                                                a={l.novo_altura} l={l.novo_largura} c={l.novo_comprimento}
                                                peso={l.novo_peso} cobravel={l.novo_cobravel} metodo={l.novo_metodo}
                                            />
                                        </Td>
                                        <Td isNumeric>
                                            <SeloRazao razao={l.razao} />
                                            <Text fontSize="10px" color="gray.500" mt={1} whiteSpace="nowrap">
                                                {(Number(l.novo_cobravel) - Number(l.antigo_cobravel)).toFixed(2)} kg
                                            </Text>
                                        </Td>
                                        <Td fontSize="10px" color="gray.500" whiteSpace="nowrap">
                                            texto {kg(l.texto_kg)}<br />foto {kg(l.visao_kg)}
                                        </Td>
                                        <Td><Badge fontSize="9px">{l.contexto}</Badge></Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Rolavel>
                )}
            </Cartao>
        </VStack>
    );
};

const FAIXA_ERRO = {
    cotou_menos: 'Cotou menos que a caixa real',
    no_alvo: 'Acertou (dentro de ±25%)',
    cotou_mais: 'Cotou mais que a caixa real',
};

const AbaConferencia = () => {
    const [dado, setDado] = useState({ resumo: {}, linhas: [] });
    const [filtros, setFiltros] = useState({ busca: '', fonte: '', metodo: '', direcao: '', dinheiro: '', so_limpas: false, de: '', ate: '' });
    const [carregando, setCarregando] = useState(true);

    const buscar = useCallback(async () => {
        setCarregando(true);
        try { setDado(await getDimensoesConferencia({ ...filtros, limite: 100 })); } finally { setCarregando(false); }
    }, [filtros]);

    useEffect(() => { buscar(); }, [buscar]);

    const set = (campo) => (e) => setFiltros((p) => ({ ...p, [campo]: e.target.value }));
    const r = dado.resumo || {};
    const vazio = !r.amostras;

    return (
        <VStack align="stretch" spacing={4}>
            <Cartao p={{ base: 4, md: 5 }}>
                <Text fontSize="sm" fontWeight="bold">A nota dos dois estimadores contra a caixa que realmente saiu</Text>
                {vazio ? (
                    <>
                        <Text fontSize="xs" color="gray.500" mt={0.5} mb={4}>
                            Aqui existe resposta certa: a caixa medida no despacho. Cada linha guarda a estimativa que
                            estava valendo <b>antes</b> da medida existir, porque assim que ela chega o produto passa a
                            usar a medida direto e a comparação perderia o sentido.
                        </Text>
                        <Box bg="gray.50" borderRadius="lg" p={5} textAlign="center">
                            <Text fontSize="sm" color="gray.600">Ainda não há nenhuma caixa medida depois de uma cotação registrada.</Text>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                O ciclo é: o cliente cota, compra, a peça é despachada e o Melhor Envio mede.
                                A coleta roda de hora em hora e o par aparece aqui em seguida.
                            </Text>
                        </Box>
                    </>
                ) : (
                    <>
                        <Text fontSize="xs" color="gray.500" mt={0.5} mb={4}>
                            {num(r.amostras)} conferências em {num(r.produtos)} produtos, contra a caixa medida no despacho.
                        </Text>

                        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>O novo acerta mais que o antigo?</Text>
                        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={3}>
                            <Indicador
                                rotulo="Novo chegou mais perto da caixa real"
                                valor={r.novo_venceu_pct != null ? `${r.novo_venceu_pct}%` : '-'}
                                cor={r.novo_venceu_pct >= 50 ? 'teal.500' : 'red.500'}
                                ajuda={`${num(r.novo_venceu)} de ${num(r.duelo_n)} comparações contra o antigo`}
                            />
                            <Cartao gridColumn={{ lg: 'span 2' }} overflow="hidden">
                                <Table size="sm">
                                    <Thead bg="gray.50">
                                        <Tr><Th></Th><Th isNumeric>Novo</Th><Th isNumeric>Antigo</Th></Tr>
                                    </Thead>
                                    <Tbody>
                                        {[
                                            {
                                                nome: 'Acertou (dentro de ±25%)', dica: 'maior é melhor', maiorMelhor: true,
                                                novo: r.no_alvo, antigo: r.no_alvo_antigo, fmt: (v) => `${v}%`,
                                            },
                                            {
                                                nome: 'Erro típico (mediana)', dica: 'menor é melhor; 1x seria acerto exato', maiorMelhor: false,
                                                novo: r.erro_mediano, antigo: r.erro_mediano_antigo, fmt: (v) => `${Number(v).toFixed(2)}x`,
                                            },
                                            {
                                                nome: 'Errou mais de 2x', dica: `menor é melhor; o novo errou ${r.subestima_2x || 0}% para baixo e ${r.superestima_2x || 0}% para cima`,
                                                maiorMelhor: false, novo: (r.subestima_2x || 0) + (r.superestima_2x || 0),
                                                antigo: r.errou_2x_antigo, fmt: (v) => `${v}%`,
                                            },
                                        ].map((m) => {
                                            const temOsDois = m.novo != null && m.antigo != null && Number(m.novo) !== Number(m.antigo);
                                            const novoMelhor = temOsDois && (m.maiorMelhor ? Number(m.novo) > Number(m.antigo) : Number(m.novo) < Number(m.antigo));
                                            const cor = (ehNovo) => (!temOsDois ? 'gray.800' : (ehNovo === novoMelhor ? 'teal.600' : 'gray.500'));
                                            return (
                                                <Tr key={m.nome}>
                                                    <Td>
                                                        <Text fontSize="sm">{m.nome}</Text>
                                                        <Text fontSize="10px" color="gray.500">{m.dica}</Text>
                                                    </Td>
                                                    <Td isNumeric fontSize="lg" fontWeight="bold" color={cor(true)} whiteSpace="nowrap">
                                                        {m.novo != null ? m.fmt(m.novo) : '-'}
                                                    </Td>
                                                    <Td isNumeric fontSize="lg" fontWeight="bold" color={cor(false)} whiteSpace="nowrap">
                                                        {m.antigo != null ? m.fmt(m.antigo) : '-'}
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                            </Cartao>
                        </SimpleGrid>

                        {r.dinheiro?.envios > 0 && (
                            <Box mt={5} pt={4} borderTopWidth="1px" borderColor="gray.100">
                                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={0.5}>Onde ganhamos e perdemos dinheiro no frete?</Text>
                                <Text fontSize="10px" color="gray.500" mb={3}>
                                    Cobrado do cliente menos pago à transportadora. Não é tudo culpa do estimador:
                                    promoção de frete, serviço escolhido e distância entram junto.
                                </Text>
                                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
                                    <Indicador
                                        rotulo="Saldo" valor={reais(r.dinheiro.saldo)}
                                        cor={r.dinheiro.saldo >= 0 ? 'teal.500' : 'red.500'}
                                        ajuda={`${num(r.dinheiro.envios)} envios com os dois valores`}
                                    />
                                    {['cotou_menos', 'no_alvo', 'cotou_mais'].map((faixa) => {
                                        const f = (r.dinheiro_por_erro || []).find((x) => x.faixa === faixa);
                                        return (
                                            <Indicador
                                                key={faixa} rotulo={FAIXA_ERRO[faixa]}
                                                valor={f ? reais(f.saldo) : '-'}
                                                cor={!f ? 'gray.400' : Number(f.saldo) >= 0 ? 'teal.500' : 'red.500'}
                                                ajuda={f ? `${num(f.n)} ${Number(f.n) === 1 ? 'envio' : 'envios'}, ${reais(f.media)} por envio` : 'nenhum envio'}
                                            />
                                        );
                                    })}
                                </SimpleGrid>
                            </Box>
                        )}

                        <Accordion allowToggle mt={5}>
                            <AccordionItem border="none">
                                <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                                    <Text flex={1} textAlign="left" fontSize="xs" fontWeight="bold" color="gray.600">Detalhes</Text>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel px={0} pb={0}>
                                    <Text fontSize="xs" color="gray.500" mb={4}>
                                        Aqui existe resposta certa: a caixa medida no despacho. Cada linha guarda a estimativa que
                                        estava valendo <b>antes</b> da medida existir, porque assim que ela chega o produto passa a
                                        usar a medida direto e a comparação perderia o sentido.
                                    </Text>

                                    <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Peso somado do erro do novo</Text>
                                    <VStack align="stretch" spacing={1.5} mb={5}>
                                        <Flex justify="space-between" fontSize="xs" gap={2}>
                                            <Text color="gray.600">Cotou menos que a caixa (é aqui que a loja perde)</Text>
                                            <Text fontWeight="bold" color="red.500" whiteSpace="nowrap">{num(r.kg_a_menos)} kg</Text>
                                        </Flex>
                                        <Flex justify="space-between" fontSize="xs" gap={2}>
                                            <Text color="gray.600">Cotou mais que a caixa (é aqui que o cliente paga a mais)</Text>
                                            <Text fontWeight="bold" color="blue.500" whiteSpace="nowrap">{num(r.kg_a_mais)} kg</Text>
                                        </Flex>
                                    </VStack>

                                    {r.dinheiro?.envios > 0 && (
                                        <>
                                            <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Frete somado</Text>
                                            <VStack align="stretch" spacing={1.5} mb={5}>
                                                <Flex justify="space-between" fontSize="xs" gap={2}>
                                                    <Text color="gray.600">Ganhamos, somado onde o frete cobriu o custo</Text>
                                                    <Text fontWeight="bold" color="teal.600" whiteSpace="nowrap">{reais(r.dinheiro.ganho)}</Text>
                                                </Flex>
                                                <Flex justify="space-between" fontSize="xs" gap={2}>
                                                    <Text color="gray.600">Perdemos, somado onde o frete ficou abaixo do custo</Text>
                                                    <Text fontWeight="bold" color="red.500" whiteSpace="nowrap">{reais(r.dinheiro.perda)}</Text>
                                                </Flex>
                                                <Flex justify="space-between" fontSize="xs" gap={2}>
                                                    <Text color="gray.600">Envios no prejuízo</Text>
                                                    <Text fontWeight="bold" whiteSpace="nowrap">
                                                        {r.dinheiro.no_prejuizo_pct != null ? `${r.dinheiro.no_prejuizo_pct}%` : '-'}
                                                        {' '}({num(r.dinheiro.no_prejuizo)} de {num(r.dinheiro.envios)})
                                                    </Text>
                                                </Flex>
                                            </VStack>
                                        </>
                                    )}

                                    {(r.por_metodo || []).length > 0 && (
                                        <>
                                            <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Por caminho que o estimador usou</Text>
                                            <VStack align="stretch" spacing={1.5}>
                                                {r.por_metodo.map((m) => (
                                                    <Flex key={m.metodo} justify="space-between" fontSize="xs" gap={2}>
                                                        <Badge fontSize="9px" fontFamily="mono">{m.metodo}</Badge>
                                                        <Text color="gray.600" whiteSpace="nowrap">
                                                            {num(m.n)} {Number(m.n) === 1 ? 'caso' : 'casos'}, razão média {Number(m.razao_media).toFixed(2)}x
                                                        </Text>
                                                    </Flex>
                                                ))}
                                            </VStack>
                                        </>
                                    )}
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                    </>
                )}
            </Cartao>

            <Cartao p={{ base: 3, md: 4 }}>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 6 }} spacing={3}>
                    <InputGroup>
                        <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                        <Input size="sm" borderRadius="lg" placeholder="Produto ou SKU" value={filtros.busca} onChange={set('busca')} />
                    </InputGroup>
                    <Select size="sm" borderRadius="lg" value={filtros.direcao} onChange={set('direcao')}>
                        <option value="">Todos os resultados</option>
                        <option value="no_alvo">Acertou (dentro de ±25%)</option>
                        <option value="a_menos">Cotou menos que a caixa</option>
                        <option value="a_mais">Cotou mais que a caixa</option>
                        <option value="errou_feio">Errou mais de 2x</option>
                    </Select>
                    <Select size="sm" borderRadius="lg" value={filtros.fonte} onChange={set('fonte')}>
                        <option value="">Toda origem da medida</option>
                        <option value="melhor_envio">Caixa do despacho</option>
                        <option value="mercado_livre">Medido pelo ML</option>
                    </Select>
                    <Select size="sm" borderRadius="lg" value={filtros.metodo} onChange={set('metodo')}>
                        <option value="">Todos os métodos</option>
                        <option value="texto_x_visao">texto_x_visao</option>
                        <option value="so_visao">so_visao</option>
                        <option value="so_texto">so_texto</option>
                        <option value="gemeo_codigo_global">gemeo_codigo_global</option>
                    </Select>
                    <Select size="sm" borderRadius="lg" value={filtros.dinheiro} onChange={set('dinheiro')}>
                        <option value="">Ganhando e perdendo</option>
                        <option value="prejuizo">So onde perdemos dinheiro</option>
                        <option value="lucro">So onde o frete cobriu</option>
                    </Select>
                    <Input size="sm" borderRadius="lg" type="date" value={filtros.de} onChange={set('de')} />
                </SimpleGrid>
                <Flex mt={3} justify="space-between" align="center" gap={2} wrap="wrap">
                    <Checkbox
                        size="sm" isChecked={filtros.so_limpas}
                        onChange={(e) => setFiltros((p) => ({ ...p, so_limpas: e.target.checked }))}
                    >
                        <Text fontSize="xs" color="gray.600">Só despachos de uma peça só (a régua mais limpa)</Text>
                    </Checkbox>
                    <Button
                        size="xs" variant="ghost" borderRadius="lg"
                        onClick={() => setFiltros({ busca: '', fonte: '', metodo: '', direcao: '', dinheiro: '', so_limpas: false, de: '', ate: '' })}
                    >
                        Limpar filtros
                    </Button>
                </Flex>
            </Cartao>

            <Cartao overflow="hidden">
                {carregando ? <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex> : (
                    <Rolavel>
                        <Table size="sm" sx={{ 'th, td': { px: 2 }, 'th:first-of-type, td:first-of-type': { pl: 4 }, 'th:last-of-type, td:last-of-type': { pr: 4 } }}>
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>Medido em</Th><Th>Venda</Th><Th>Produto</Th>
                                    <Th>Caixa real</Th><Th>Antigo</Th><Th>Novo</Th>
                                    <Th isNumeric>Novo x real</Th><Th>Quem acertou</Th>
                                    <Th isNumeric>Frete</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {dado.linhas.map((l) => {
                                    const dNovo = Math.abs(Math.log(Number(l.razao_novo) || 1));
                                    const dAntigo = l.razao_antigo > 0 ? Math.abs(Math.log(Number(l.razao_antigo))) : null;
                                    const vencedor = dAntigo == null ? null : (dNovo < dAntigo ? 'novo' : 'antigo');
                                    return (
                                        <Tr key={l.id} _hover={{ bg: 'gray.50' }}>
                                            <Td whiteSpace="nowrap">
                                                <Text fontSize="xs">{dataCurta(l.medido_em)}</Text>
                                                <Text fontSize="10px" color="gray.500">{String(l.medido_em || '').slice(11, 16)}</Text>
                                            </Td>
                                            <Td whiteSpace="nowrap">
                                                {l.venda_id ? (
                                                    <>
                                                        <Text fontSize="xs" fontWeight="medium">#{l.venda_id}</Text>
                                                        <Text fontSize="9px" color="gray.500">
                                                            {l.venda_origem}{l.uf ? ` - ${l.uf}` : ''}
                                                        </Text>
                                                        {l.cep && <Text fontSize="9px" color="gray.400" fontFamily="mono">{l.cep}</Text>}
                                                    </>
                                                ) : <Text fontSize="10px" color="gray.400">-</Text>}
                                            </Td>
                                            <Td minW="160px" maxW="240px">
                                                <Text fontSize="sm">{l.nome}</Text>
                                                <Flex gap={1} mt={0.5} wrap="wrap">
                                                    <Badge fontSize="9px" fontFamily="mono">#{l.produto_id}</Badge>
                                                    {!l.limpo && <Badge fontSize="9px" colorScheme="gray">envio com mais itens</Badge>}
                                                </Flex>
                                            </Td>
                                            <Td>
                                                <CaixaEstimativa
                                                    a={l.real_altura} l={l.real_largura} c={l.real_comprimento}
                                                    peso={l.real_peso} cobravel={l.real_cobravel} metodo={l.fonte}
                                                />
                                            </Td>
                                            <Td minW="140px"><Text fontSize="xs" color="gray.600">{kg(l.antigo_cobravel)}</Text>
                                                <Badge fontSize="9px" fontFamily="mono" whiteSpace="normal" maxW="130px">
                                                    {String(l.antigo_metodo || '').replace(/_/g, '_​')}
                                                </Badge></Td>
                                            <Td>
                                                <CaixaEstimativa
                                                    a={l.novo_altura} l={l.novo_largura} c={l.novo_comprimento}
                                                    peso={l.novo_peso} cobravel={l.novo_cobravel} metodo={l.novo_metodo}
                                                />
                                            </Td>
                                            <Td isNumeric>
                                                <SeloRazao razao={l.razao_novo} />
                                                <Text fontSize="10px" color="gray.500" mt={1} whiteSpace="nowrap">
                                                    {(Number(l.novo_cobravel) - Number(l.real_cobravel)).toFixed(2)} kg
                                                </Text>
                                            </Td>
                                            <Td>
                                                {vencedor === null
                                                    ? <Text fontSize="10px" color="gray.400">sem o antigo</Text>
                                                    : <Badge fontSize="9px" colorScheme={vencedor === 'novo' ? 'teal' : 'gray'}>
                                                        {vencedor === 'novo' ? 'novo' : 'antigo'}
                                                    </Badge>}
                                            </Td>
                                            <Td isNumeric whiteSpace="nowrap">
                                                {l.frete_resultado == null ? (
                                                    <Text fontSize="10px" color="gray.400">sem os dois valores</Text>
                                                ) : (
                                                    <>
                                                        <Text fontSize="sm" fontWeight="bold" color={Number(l.frete_resultado) >= 0 ? 'teal.600' : 'red.500'}>
                                                            {reais(l.frete_resultado)}
                                                        </Text>
                                                        <Text fontSize="9px" color="gray.500">cobrou {reais(l.frete_cobrado)}</Text>
                                                        <Text fontSize="9px" color="gray.500">pagou {reais(l.frete_pago)}</Text>
                                                    </>
                                                )}
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Rolavel>
                )}
            </Cartao>
        </VStack>
    );
};

const ABAS = [
    { titulo: 'Métricas', icone: FaChartBar, componente: AbaPlacar },
    { titulo: 'Simulador', icone: FaFlask, componente: AbaSimulador },
    { titulo: 'Banco', icone: FaBoxOpen, componente: AbaBanco },
    { titulo: 'Famílias', icone: FaLayerGroup, componente: AbaFamilias },
    { titulo: 'Suspeitos', icone: FaExclamationTriangle, componente: AbaSuspeitos },
    { titulo: 'Logs', icone: FaHistory, componente: AbaLogs },
    { titulo: 'Cotações', icone: FaBalanceScale, componente: AbaCotacoes },
    { titulo: 'Conferência', icone: FaClipboardCheck, componente: AbaConferencia },
    { titulo: 'Configurações', icone: FaCog, componente: AbaConfiguracoes },
];

const Dimensoes = () => (
    <Box bg="gray.50" minH="100vh">
        <ScrollToTop />

        <Box bg="white" borderBottom="1px solid" borderColor="gray.100" px={{ base: 4, md: 8 }} py={4}>
            <Flex align="center" maxW="1300px" mx="auto" gap={3}>
                <Box bg="orange.500" p={2} borderRadius="lg" flexShrink={0}>
                    <Icon as={FaRulerCombined} color="white" boxSize={5} />
                </Box>
                <Text fontSize="lg" fontWeight="bold" color="gray.800">Dimensões</Text>
            </Flex>
        </Box>

        <Box maxW="1300px" mx="auto" px={{ base: 3, md: 8 }} py={{ base: 4, md: 6 }}>
            <Tabs colorScheme="orange" variant="soft-rounded" isLazy>
                <Box bg="white" borderRadius="2xl" boxShadow="sm" border="1px solid" borderColor="gray.100" mb={4}>
                    <Box px={{ base: 3, md: 5 }} py={3}>
                        <TabList gap={1} overflowX="auto" pb={1}>
                            {ABAS.map((aba) => (
                                <Tab
                                    key={aba.titulo} fontWeight="semibold" fontSize="sm" borderRadius="lg"
                                    px={4} py={2} flexShrink={0}
                                    _selected={{ bg: 'orange.500', color: 'white' }}
                                >
                                    <Icon as={aba.icone} mr={2} boxSize={3} />{aba.titulo}
                                </Tab>
                            ))}
                        </TabList>
                    </Box>
                </Box>

                <TabPanels>
                    {ABAS.map((aba) => (
                        <TabPanel key={aba.titulo} px={0} pt={0}>
                            <aba.componente />
                        </TabPanel>
                    ))}
                </TabPanels>
            </Tabs>
        </Box>
    </Box>
);

export default Dimensoes;
